import { bridge, detailState } from './bridge';
import {
  CLEAR, clearPendingConfirms, comboOptions, fillConditions, isBlockingNotice, openScreen,
  pressNamed, runAction, runAutoAction, runDraftAction, sendMessage,
} from './actions';
import type { SendChannel } from './actions';
import { findDraftField, findField, inputOf } from './anchors';
import { hasSearchPopup, pairedName, pickFromSearchPopup, sameName } from './popup';
import { OptionNotFound } from './input';
import { similarity } from './hangul';
import { changedArrays, readRows, readScalars } from './rows';
import {
  openTab as pressTab, fillGridRows, fillRowCells, listTotal, locateCell, pressRowButton, unfoldList,
} from './grid';
import { openCalendarDay, pickDate } from './calendar';
import { applyWindowPick, prepareWindowPick } from './windowPick';
import type { WindowPending, WindowPickSpec } from './windowPick';
import { closeInfoModals, dismissNotices } from './dialogs';
import { appliedConditions, snapshotUi } from './snapshot';
import type { UiSnapshot } from './snapshot';
import { onScreen, onScreenAll } from './visible';
import { closeWaitQueueWindow, queuedSince, waitQueue } from './waitQueue';
import { approveOne } from './approval';
import type { ApprovalAction, ApprovalResult } from './approval';
import type { QueuedJob } from './waitQueue';

/** 화면 고유 버튼을 눌러 문서를 잇는 요청. 견적서의 `자재청구서` 버튼이 그렇다. */
export type OperateRequest = {
  routePath: string;
  /** 화면에 적힌 버튼 이름 그대로. */
  button: string;
  auto: boolean;
  /** 모달에서 눌러야 하는 버튼. 기본은 `확인`. */
  confirm?: string;
  /** 창이 요구하는 값. 확인을 누르기 전에 채운다. */
  fields?: Record<string, string>;
  /** 창 안 표에서 전부 고를 상태 키. `확인: 선택을 하나이상 하십시요` 를 막는다. */
  selectKey?: string;
  /**
   * **누르기 전에 고를 목록 행.** 문서 단추(결재상신)는 화면이 들고 있는 그 문서에 일한다. 저장 직후의
   * 업무일지는 서버에서 다시 읽은 문서가 아니라서 상신이 「본인이 작성하지 않은 문서는 결재할 수
   * 없습니다」로 막혔다(개발 테넌트, 2026-10-13 — 사람도 4번 막혔다). 목록에서 그 행을 골라 다시 읽힌다.
   */
  focus?: { stateKey: string; match: Record<string, string>; conditions?: Record<string, string>; then?: InnerRow };
  /**
   * **표 안에 달린 버튼일 때.** 이게 있으면 `button` 은 사람에게 보여 줄 이름일 뿐이고,
   * 실제로 누르는 것은 그 행 그 칸의 버튼이다.
   *
   * 왜 따로 두나 — 이 버튼들은 대개 글자가 없는 아이콘이라 이름으로 못 찾는다. 그리고
   * 행마다 하나씩이라 **어느 행인지**를 골라야 한다. 점검결과등록의 `점검일지` 가 그
   * 모양이고, 앱 전체에 154개·화면 60곳이다(2026-09-21 실측).
   *
   * `column` 은 카탈로그의 `grids[].rowButtons[].column` — 헤더 `id` 이고 DOM 의
   * `data-el-cell` 과 같다. 표의 컬럼 목록에 적힌 이름과는 다를 수 있다.
   */
  row?: {
    stateKey: string;
    match: Record<string, string>;
    column: string;
    nth?: number;
    /**
     * **누르기 전에 걸 조회 조건.** 표가 비어 있으면 고를 행이 없다.
     *
     * 화면을 열면 표는 비어 있거나 다른 달이 담겨 있다. 실측(2026-09-21): 계획이
     * 조회 없이 바로 `점검일지` 를 눌러 「표가 비었다」로 끝났다. `sequence` 는 쓰기
     * 전용이라 조회와 누르기를 한 걸음에 담을 수 없어, 이 자리에서 먼저 조회한다.
     */
    conditions?: Record<string, string>;
  };
  /**
   * **누르기 전에 화면 목록을 이 조건으로 조회하고, `check` 면 그 표의 행을 고른다.**
   *
   * 지로일괄발행의 `전자지로생성` 은 고른 행(`chk='1'`)만 서버에 맡긴다. 조회 없이 누르면
   * 화면의 기본 달(자료 없음)인 채로 눌러 아무 일도 안 났는데 「눌렀습니다」로 답했다
   * (개발 테넌트, 2026-10-12). 조회해서 행이 없으면 누르지 않는다.
   */
  list?: {
    stateKey: string; conditions?: Record<string, string>; check?: boolean;
    /** 체크 칸 이름. 기본 `chk` — 전자세금계산서발행은 `selchk` 다. */
    checkKey?: string;
    /** 이 값들로 시작하는 줄만 체크한다(거래처 이름 등). 없으면 목록 전부. */
    match?: Record<string, string>;
  };
};

/** 저장·삭제 요청. `auto` 는 사용자가 패널에서 켠 값 그대로다. */
export type CommitRequest = {
  routePath: string;
  auto: boolean;
  action?: 'save' | 'delete';
};

export type RetrieveRequest = {
  label: string;
  routePath: string;
  /** 카탈로그가 아는 경우에만. 없으면 조회 전후 차이로 찾는다. */
  rowsStateKey?: string;
  scalarKeys?: string[];
  conditions: Record<string, string>;
  /** openChk 같은 표 기반 필수 선택목록. 조회 직전에 전부 체크한다. */
  selectionStateKeys?: string[];
  /** 사업장 전환 뒤 같은 조건이라도 서버를 다시 조회한다. */
  force?: boolean;
};

export type Retrieved = {
  label: string;
  routePath: string;
  /** 실제로 읽은 키. 카탈로그가 몰라 차이로 찾아낸 경우 그 결과가 여기 남는다. */
  rowsStateKey: string;
  rows: Record<string, unknown>[];
  totals?: Record<string, unknown>;
  /** 조회 중에 떠서 닫은 알림들. 사용자가 못 본 것을 답변이 알려 줄 수 있어야 한다. */
  notices?: string[];
  /** 값이 제거된 화면 구조. 성공 경험 후보와 회귀 진단에 쓴다. */
  snapshot?: UiSnapshot;
  /** 조회 때 화면 조건 칸에 걸려 있던 값. 계획이 비워 둔 칸의 화면 기본값을 계산이 알게 한다(경험 후보에는 안 싣는다). */
  applied?: Record<string, string>;
  /**
   * **서버가 이 조회에 대답했는가.** 거짓이면 0행은 「자료가 없다」가 아니다.
   *
   * 화면은 조회를 누르면 표를 먼저 비우고 서버 응답이 오면 채운다. 서버가 조용하면
   * 표는 빈 채로 남고 `busy` 는 참으로 붙어 있다 — 그대로 마감이 지나면 우리는 빈
   * 배열을 읽고 **「조건에 맞는 자료가 없습니다」라고 확신을 갖고 답한다.** 사람은
   * 그 말을 믿고 일한다. 못 하는 것보다 나쁘다.
   *
   * 그래서 「마감이 지났는데 아직 바빴다」를 따로 들고 나온다. 프레임을 뜯을 필요가
   * 없다 — `busy` 가 안 걷혔다는 것으로 충분하다.
   *
   * 빠져 있으면(`undefined`) 대답을 받은 것으로 본다. 기다릴 일이 없던 길에서는
   * 애초에 이 물음이 생기지 않는다.
   */
  answered?: boolean;
  /** 펼쳐 읽기로 받았으면 범위 안 위 표 행. 상세가 빈 행(경력 없는 사람)도 여기에는 있다. */
  masterRows?: Record<string, unknown>[];
  /** 펼쳐 읽기(`sweepDetail`)로 받았으면 위 표 행 수와 읽은 행 수, 다 못 읽은 까닭. */
  swept?: {
    masterRows: number;
    read: number;
    /** 고르지 못한 위 표 행 수. */
    missed?: number;
    /** 골랐는데 상세가 끝내 안 바뀌어 0행으로 친 위 표 행 수. */
    unchanged?: number;
    /** 좁힌 값(`only`) 중 위 목록에 없던 수 — 퇴사자 등. */
    unmatched?: number;
    reason?: string;
  };
};

/**
 * **위 표 행마다 아래 상세 표를 읽는다.** 일근태등록은 위 표가 날짜, 아래 표가 그날 직원별 출근이라
 * 「10/15 전후 출근」을 보려면 날마다 골라 읽어야 한다. 지출결의서·발주등록 같은 문서 목록도 같은 꼴이다.
 */
export type SweepRequest = {
  label: string;
  routePath: string;
  masterStateKey: string;
  detailStateKey: string;
  conditions: Record<string, string>;
  /**
   * 위 표 행을 이 칸 값으로 거른다(양끝 포함). 숫자만 남겨 경계의 자리수만큼 견준다 —
   * `2026-10-15` 는 `20261015` 로, `to: '202610'` 은 10월 끝날까지다.
   */
  range?: { key: string; from?: string; to?: string };
  /**
   * 위 표 행을 이 칸 값들로 거른다. 앞 조회가 뽑은 대상만 읽는다 — 최근 입사자의 사번만 인사등록에서
   * 펼치면 수십 명을 다 고르지 않아도 된다(한 사람에 1초 남짓, 2026-10-21 개발 테넌트).
   */
  only?: { key: string; values: string[] };
  /** 위 표에서 행을 고를 때 맞출 칸들. 없으면 그 행의 글자·숫자 칸 전부로 맞춘다. */
  matchKeys?: string[];
  /** 위 표 행이 이보다 많으면 읽지 않는다. */
  limit?: number;
  /** 한 행의 상세를 기다리는 시간. 기본 `SWEEP_ROW_MS`. */
  rowMs?: number;
  /**
   * 상세가 이 탭에만 있으면 위 표를 받은 뒤 먼저 연다. 인사등록은 행을 고르면 **열린 탭**의 자료만
   * 받아 온다 — 기본정보 탭인 채로 고르면 경력이 안 온다.
   */
  tab?: string;
};

export type PrepareRequest = {
  /** 달력 화면에서 먼저 열 날짜(여덟 자리). 그 날 창 안에서만 행을 더할 수 있다. */
  calendarDay?: string;
  routePath: string;
  fields: Record<string, string>;
  /**
   * 목록에서 고르는 칸의 **고를 수 있는 값**. 칸 이름 → 코드와 이름 전부.
   *
   * 드라이버는 무엇이 옳은지 모른다 — 시키는 것만 지킨다. 판단은 계획이 하고, 여기서는
   * 타이프하기 **전에** 막는 일만 한다. 목록 밖 값을 넣으면 검색 팝업이 비슷한 것을
   * 골라 저장까지 된다 — `로프` 를 넣었더니 `휠체어리프트` 가 들어간 적이 있다.
   * 그때는 사람이 나중에 발견한다.
   */
  allowed?: Record<string, string[]>;
  /**
   * 코드 칸과 짝이 되는 이름 칸. `{ gregicd: 'greginm' }` 꼴이다.
   *
   * 없으면 접미사(`cd`→`nm`)로 짐작한다. 그 짐작은 규약을 어기는 화면에서 조용히
   * 틀리는데, 초안 판정이 그 값을 보고 「우리가 넣은 것이 남았나」를 가른다.
   */
  pairs?: Record<string, string>;
  /** 직전 AI 초안을 보정하는 후속 발화면 새 행을 만들지 않고 그 임시 행을 다시 쓴다. */
  continueDraft?: boolean;
  /**
   * **이미 있는 문서를 고쳐 쓴다.** 새로 만들지 않는다.
   *
   * 고장완료가 그렇다 — 접수는 이미 있고, 그 행을 골라 처리결과를 채워 저장한다.
   * `reuse` 와 다르다: 그쪽은 지난 문서를 **읽어 베끼고** 새로 만든다.
   */
  edit?: {
    rowsStateKey: string;
    match: Record<string, string>;
    /** 고른 문서 안의 표에서 한 줄을 더 고른다 — 보수현장의 호기(`units`). */
    then?: InnerRow;
    /** 그 행의 칸을 고친다. 점검결과는 표의 `결과` 칸을 `처리` 로 바꾸는 일이다. */
    cells?: Record<string, string>;
    /**
     * **헤더 칸을 표 칸보다 먼저** 채운다. 입금입력은 입금액 합계가 0이면 미수 체크를
     * 거부한다(`checkAlert`) — 표 먼저면 체크가 늘 막혔다(2026-09-29). 기본은 표 먼저다:
     * 고장완료처럼 행을 골라야 헤더가 그 문서로 바뀌는 화면이 더 많다.
     */
    fieldsFirst?: boolean;
    /**
     * **칸을 고르는 순간 화면이 서버에 반영한다.** 준비에서는 그 행까지만 짚고 칸은 건드리지
     * 않는다. 칸은 확정(`commit`)에서 바꾼다 — 저장과 같은 게이트다.
     *
     * 점검계획의 점검일자가 그렇다. 달력에서 날을 누르면 저장 단추 없이 곧바로 옮겨지고
     * `완료되었습니다` 가 뜬다(2026-09-30 개발 테넌트). 준비에서 채우면 Auto 가 꺼져 있어도
     * 옮겨져 버린다.
     *
     * 목록(`rowsStateKey`)이 달력이면 그 행의 날을 열어 창 안의 표(`cellsStateKey`)에서
     * 그 행을 찾는다.
     */
    deferCells?: boolean;
    /**
     * **목록 줄에서 창을 열고 창 안에서 줄을 골라 창 단추로 확정하는 일.** 예금관리의 입금처리가
     * 그렇다 — 처리 체크를 켜면 미수 창이 뜨고, 미수를 고르고, 창의 `확인` 이 입금처리다.
     * 준비는 창을 열고 고르기까지, 단추는 확정(Auto)이 누른다. `cellsStateKey` 가 창 안 표,
     * `cellsMatch` 가 사람이 말한 창 안 줄이다. 무엇을 누르는지는 스킬이 정한다(`windowPick.ts`).
     */
    window?: WindowPickSpec;
    /**
     * 사용자가 고른 행의 이름 그대로. 되묻기에 답한 턴이 이것을 들고 온다.
     *
     * 조건으로 다시 짜 맞추려 하면 `덕봉빌딩(두암정철) 20250206` 을 거래처 이름으로
     * 읽는 식의 오해가 난다 — 우리가 내민 글자를 그대로 되받는 편이 확실하다.
     */
    pick?: string;
    /**
     * 고칠 문서를 불러오는 조회 조건.
     *
     * 기본 조건은 대개 이번 달이다 — 7월 점검을 고치려면 `planmon` 을 7월로 두고
     * 조회해야 그 행이 목록에 올라온다. 없으면 화면의 기본 조건으로 조회한다.
     */
    conditions?: Record<string, string>;
    /**
     * 칸을 고칠 표. 문서를 고르는 표와 다를 수 있다.
     *
     * 출장보고서는 목록(`tripList`)에서 문서를 고르고 **상세(`tripDetailList`)의 칸**을
     * 고친다. 하나뿐이던 시절에는 그 둘을 나눌 수 없어, 교통비를 고쳐 달라는 말에
     * 교통비 줄이 하나 더 붙었다.
     */
    cellsStateKey?: string;
    /** 그 표 안에서 고칠 행. 없으면 문서를 고른 그 값으로 짚는다. */
    cellsMatch?: Record<string, string>;
  };
  reuse?: {
    rowsStateKey: string;
    match: Record<string, string>;
    fields: string[];
  };
  /**
   * 헤더만으로는 쓸모없는 문서(견적서 등)의 상세행.
   *
   * 단가·금액은 넣지 않는다 — 품목과 수량을 넣으면 화면이 스스로 계산한다.
   */
  rows?: {
    stateKey: string;
    items: Record<string, string>[];
    tab?: string;
    addLabel?: string;
  };
};

export type Prepared = {
  routePath: string;
  fields: Record<string, string>;
  /**
   * 확정으로 미룬 칸. 준비는 그 행까지 짚었고 칸은 그대로다. `row` 는 사람에게 보일 행 이름
   * (`덕봉빌딩(두암정철) 2026-09-25`), `cells` 는 확정에서 넣을 값이다.
   */
  deferred?: {
    row: string; cells: Record<string, string>;
    /** 행 이름에서 날을 뺀 것(`덕봉빌딩(두암정철)`)과 바꾸기 전 값. 답을 사람 말로 짓는 데 쓴다. */
    name?: string; before?: Record<string, string>;
    /** 창에서 고르고 단추는 미룬 일. `picked` 는 창에서 고른 줄의 이름이다. */
    window?: { action: string; button: string; picked: string };
  };
};

/** 조회 마감 기본값. 부르는 쪽이 따로 주지 않으면 이것이다 — `retrieve` 의 주석을 보라. */
const RETRIEVE_MS = 45_000;
const POLL_MS = 25;
/** 조회를 누르기 전에 화면이 이만큼 계속 조용해야 멎었다고 본다 — `retrieve` 의 주석을 보라. */
const QUIET_MS = 600;
// 팝업을 열어 후보만 읽을 때 골라 달라 하는, 있을 리 없는 값. actions.ts 의 `NOTHING`
// 과 같은 수법이다 — 못 골랐다는 오류가 들고 오는 후보(와 이유)를 받는다.
const POPUP_PROBE = '없을리없는값즤즤';
// 바뀐 뒤 이만큼 그대로여야 끝난 것으로 본다.
const SETTLE_MS = 400;
// 조회를 누른 뒤 앱이 바빠지기까지 기다려 주는 시간.
const BUSY_GRACE_MS = 1500;
/** 나눠 보여 주는 목록을 끝까지 펼치는 데 주는 시간. 쪽마다 붙는 데 한 번 그리기면 된다. */
const UNFOLD_MS = 60_000;
/** 펼쳐 읽기 전체 시한과, 위 표 한 행의 상세를 기다리는 상한. */
const SWEEP_MS = 120_000;
const SWEEP_ROW_MS = 8_000;
const SWEEP_LIMIT = 62;

/** 위 표 행에서 글자·숫자 칸만. 행 고르기와 상세 행에 붙일 값으로 쓴다. */
function plainCells(row: Record<string, unknown>): Record<string, string> {
  return Object.fromEntries(Object.entries(row)
    .filter(([, value]) => typeof value === 'string' || typeof value === 'number')
    .map(([key, value]) => [key, String(value)]));
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => { setTimeout(resolve, ms); });
}

/**
 * 조건 값이 같은가. **날짜·숫자는 구분자를 빼고 본다** — 달 칸은 화면에 `2026-10` 으로 보이고 계획은 `202610` 을
 * 낸다. 글자로만 비교해 같은 조건인데도 다시 조회했다(점검결과등록, 사용자 2026-10-15: 「이미 조회돼 열려 있는데
 * 다시 조회」).
 */
export function sameCondition(have: string, want: string): boolean {
  if (have === want) return true;
  // 날짜 꼴(여섯 자리 달·여덟 자리 날짜)만이다. `1-1` 호기와 `11` 호기를 같다고 보면 남의 호기를 읽는다.
  const dateLike = (value: string) => /^\d{4}[-./]?\d{2}([-./]?\d{2})?$/.test(value.trim());
  return dateLike(have) && dateLike(want) && have.replace(/\D/g, '') === want.replace(/\D/g, '');
}

function conditionsMatch(conditions: Record<string, string>): boolean {
  const state = bridge().pageState();
  for (const [name, wanted] of Object.entries(conditions)) {
    const field = findField(name);
    const input = field && inputOf(field);
    const inState = Object.prototype.hasOwnProperty.call(state ?? {}, name);
    // 입력칸이 숨겨져도 화면이 로그인 사용자 같은 조건을 상태에 이미 넣을 수 있다.
    // 단, 키 자체가 없는데 빈 문자열이 우연히 같다고 통과시키지는 않는다.
    if (!input && !inState) return false;
    // `(비움)` 은 비어 있기를 바란다는 뜻이다.
    const want = wanted === CLEAR ? '' : wanted;
    // 화면 상태(모델 값)가 있으면 그것이 조회에 실제로 쓰이는 값이다. 입력칸은 꾸며 보인 값일 수 있다.
    const byState = inState && sameCondition(String(state?.[name] ?? ''), want);
    const byInput = Boolean(input) && sameCondition(input!.value, want);
    if (!byState && !byInput) return false;
  }
  return true;
}

function selectionsMatch(keys: string[] | undefined): boolean {
  if (!keys?.length) return true;
  const state = bridge().pageState() ?? {};
  return keys.every((key) => {
    const rows = state[key];
    return Array.isArray(rows) && rows.length > 0 && rows.every((row) => (
      row && typeof row === 'object' && ['1', 'true'].includes(String((row as Record<string, unknown>).chk))
    ));
  });
}

/**
 * 지금 그 사업장 문맥에 있는가.
 *
 * 선택지의 `current` 로만 보면, 선택지에 없는 문맥(전사 `ZZ`)으로 되돌아온 것을
 * 영영 못 알아본다. 앱이 현재 코드를 알려 주면 그것도 같이 본다.
 */
function onBusinessSite(code: string): boolean {
  if (bridge().currentBusinessSite?.() === code) return true;
  return Boolean(bridge().businessSites?.().some((site) => site.code === code && site.current));
}

function normalizedDraftValue(value: unknown): string {
  return String(value ?? '').replace(/[^0-9A-Za-z가-힣]/g, '');
}

/**
 * 코드 칸과 짝이 되는 이름 칸의 값.
 *
 * 이 앱은 `actcd`/`actnm`, `perid`/`pernm`, `pcode`/`pname` 처럼 코드와 이름을 나란히
 * 둔다. 사람이 말하는 것은 이름이고 화면에 남는 것은 코드라, 이름 칸이 유일한 잣대다.
 */
/**
 * 이 이름 칸과 짝이 되는 **코드 칸**의 키. 같은 요청에 둘 다 들어 있을 때만 짝이다.
 *
 * 계획은 `현장코드 + 현장명` 을 함께 내곤 한다. 이름을 코드보다 먼저 적으면 팝업이
 * 그 이름을 지우고, 코드보다 나중에 적으면 등록된 현장의 코드를 화면이 지운다.
 * 그래서 이름 칸은 **코드가 안 잡혔을 때만** 적는 자리로 둔다.
 */
function pairedCode(key: string, fields: Record<string, string>): string | null {
  const stem = key.replace(/(nm|name)$/i, '');
  if (stem === key) return null;
  for (const suffix of ['cd', 'id', 'code']) {
    const candidate = `${stem}${suffix}`;
    if (Object.prototype.hasOwnProperty.call(fields, candidate)) return candidate;
  }
  return null;
}


/** 넣었지만 화면 상세 상태에 남지 않은 필드들. 입력칸이 없는 위젯은 판단하지 않는다. */
/**
 * 목록에 남아 있는 미저장 행의 표 이름.
 *
 * 되묻기 다음 턴에는 상세 상태가 비어 보여도 목록에는 그 행이 그대로 있다. 그것을
 * 못 보고 신규를 누르면 화면이 "추가하는 기능이 없습니다" 로 막는다 — 실측한 실패다.
 */
/** 어느 행에나 있는, 그 행이 무엇인지 말해 주지 않는 값들. */
const ROW_BOILERPLATE = new Set(['custcd', 'spjangcd', 'new']);

/**
 * 사람이 무언가를 적어 넣은 행인가.
 *
 * 저장한 뒤 **빈 뼈대 행을 하나 남기는** 화면이 있다. 그것을 미저장 문서로 세면
 * 잘 저장된 문서를 "저장되지 않았습니다" 로 답한다.
 */
function filledRow(row: Record<string, unknown>): boolean {
  return Object.entries(row)
    .some(([key, value]) => !ROW_BOILERPLATE.has(key) && String(value ?? '').trim() !== '');
}

/**
 * 이 행이 **우리가 방금 쓴 그 문서**인가.
 *
 * 고장접수는 저장한 뒤 새 뼈대를 남기는데 그 뼈대에는 오늘 날짜·접수번호·내 이름이
 * 이미 채워져 있다. "빈 행이 아니면 미저장" 으로는 갈리지 않는다 — 접수번호 001 이
 * 실제로 생긴 뒤에도 저장 실패로 답했다.
 *
 * 우리가 넣은 값이 그 행에 아직 남아 있는지가 유일하게 확실한 잣대다. 이름으로 넣고
 * 코드가 들어간 칸은 짝이 되는 이름 칸으로 잰다(`actcd` → `actnm`).
 */
/**
 * 그 값이 이 문서를 **가리키는** 값인가.
 *
 * 날짜·시각은 아니다. 화면이 새 행에 오늘 날짜를 미리 넣어 두기 때문에, 그것으로
 * 재면 방금 저장한 문서와 화면이 방금 깐 뼈대가 같아 보인다 — 실제로 그랬다.
 */
function identifying(value: string): boolean {
  const clean = normalizedDraftValue(value);
  return clean.length > 0 && !/^\d+$/.test(clean);
}

function carriesDraft(
  row: Record<string, unknown>,
  draft: Record<string, string>,
  pairs?: Record<string, string>,
): boolean {
  return Object.entries(draft).filter(([, wanted]) => identifying(wanted)).some(([key, wanted]) => {
    const want = normalizedDraftValue(wanted);
    const held = normalizedDraftValue(row[key]);
    if (held && held === want) return true;
    const named = pairedName(key, row, pairs);
    return Boolean(named && normalizedDraftValue(named) === want);
  });
}

/**
 * 화면이 저장을 **거절했다**고 말한 문구인가.
 *
 * 고장접수는 목록 행에 우리 값을 담지 않는다 — 저장에 성공하든 실패하든 목록에는
 * 화면이 깐 뼈대만 있어서 상태만으로는 갈리지 않았다. 거절 문구는 저장이 안 됐을
 * 때만 나오므로 **실패 증거로만** 쓴다. 성공 문구는 여전히 증거로 쓰지 않는다.
 */
function refusedNotice(notice: string): boolean {
  // 요구하는 말은 여러 꼴로 온다 — `필수입력확인:`, `경고:`, `점검일자오류:`,
  // `특이사항 미입력: … 기재하세요!`. 마지막 것을 못 읽어 저장되지 않은 점검을
  // 저장했다고 답했다(재조회하니 그대로였다).
  return /필수입력확인|경고|오류|미입력|입력하|기재하|선택하|변경해|확인해주세요/.test(notice);
}

/**
 * 화면이 **저장했다고** 말한 알림. 이것만으로 저장을 판정하지는 않는다(위 거절과 반대로 속는
 * 화면이 있다) — 옛 규칙의 판정에 증거가 있었는지 가릴 때만 쓴다. 문구는 운영 사용 기록의
 * 확정 알림에서 모았다(`events/lib/learn.mjs` 의 SAVED 와 같다).
 */
function savedNotice(notice: string): boolean {
  return /잘 저장|저장(되었|되엇|하였|했)|일괄 저장되|처리되었|완료되었|등록되었/.test(notice)
    && !/오류|실패/.test(notice);
}

function unsavedRowKey(
  draft?: Record<string, string>,
  pairs?: Record<string, string>,
): string | null {
  const state = bridge().pageState() ?? {};
  for (const [key, value] of Object.entries(state)) {
    if (!Array.isArray(value)) continue;
    const has = value.some((row) => {
      if (!row || typeof row !== 'object') return false;
      const record = row as Record<string, unknown>;
      if (String(record.new ?? '') !== '1') return false;
      // 초안이 날짜·수량뿐이면 가리킬 값이 없다. 그때는 예전 잣대로 돌아간다.
      const pointed = draft && Object.values(draft).some(identifying);
      return pointed ? carriesDraft(record, draft!, pairs) : filledRow(record);
    });
    if (has) return key;
  }
  return null;
}

/**
 * 그 조건에 맞는 행의 **실제 값들**. 못 찾으면 없다.
 *
 * 글자가 똑같은 행을 먼저 찾고, 없으면 사람이 줄여 말한 것으로 본다
 * (`덕봉빌딩` = `덕봉빌딩(두암정철)`, `1-2호기` = `1-2`).
 */
/** 우리가 내민 이름 그대로 고른 행. 그 행의 실제 값들을 돌려준다. */
function pickedRow(stateKey: string, pick?: string): Record<string, string> | null {
  if (!pick?.trim()) return null;
  const rows = rowsOf(stateKey);
  const labels = rowLabels(rows);
  const index = labels.findIndex((label) => label === pick.trim());
  if (index < 0) return null;
  const row = rows[index] ?? {};
  return Object.fromEntries(Object.entries(row)
    .filter(([, value]) => String(value ?? '').trim())
    .map(([key, value]) => [key, String(value)]));
}

/**
 * **고른 문서 안의 표에서 고를 한 줄.** 보수현장은 현장을 고르면 호기 표(`units`)가 뜨고, 승강기번호·
 * 정보센터연동은 그 표에서 고른 호기에 일한다(앱 `unitFocused`). 현장만 고르면 첫 호기에 한다.
 */
export interface InnerRow { stateKey: string; match: Record<string, string> }

function matchingRow(
  stateKey: string,
  match: Record<string, string>,
): Record<string, string> | null {
  const keys = Object.keys(match);
  const pick = (row: Record<string, unknown>) => Object.fromEntries(
    keys.map((key) => [key, String(row[key] ?? '')]),
  );
  const rows = rowsOf(stateKey);
  const exact = rows.filter((row) => keys.every((key) => String(row[key] ?? '') === match[key]));
  if (exact.length === 1) return pick(exact[0]!);
  const close = exact.length
    ? exact
    : rows.filter((row) => keys.every((key) => sameName(String(row[key] ?? ''), match[key] ?? '')));
  if (close.length === 1) return pick(close[0]!);
  // 여럿이 걸리면 고르지 않는다. 어느 호기인지는 화면을 보는 사람이 안다 — 가정으로
  // 묻지 말고 **목록에 있는 것**을 그대로 내민다.
  if (close.length > 1) {
    throw new OptionNotFound(
      Object.values(match).filter(Boolean).join(' '),
      rowLabels(close.slice(0, 10)),
    );
  }
  // 하나도 안 맞는데 목록에는 다른 것들이 있다. 그 사실을 말해야 사람이 다음 수를 안다 —
  // `7월 자비요양원` 을 찾았지만 7월에는 다른 현장뿐이었다.
  if (rows.length) {
    throw new OptionNotFound(
      Object.values(match).filter(Boolean).join(' '),
      rowLabels(rows.slice(0, 10)),
    );
  }
  return null;
}

/**
 * 사람에게 내밀 행 이름. 무엇이 다른지 보이게 호기·날짜를 함께 적는다.
 *
 * 이름이 될 만한 칸은 화면마다 다르다 — 출장보고서에는 `actnm` 도 `recenum` 도 없고
 * `title`·`outdate` 가 있다. 목록에 없는 화면에서는 **행 JSON 이 그대로 사용자에게
 * 나갔다**(`{"custcd":"weberp","spjangcd":"ZZ",…`). 사무직은 그것을 고를 수 없다.
 */
const ROW_NAME_KEYS = [
  'actnm', 'cltnm', 'title', 'subject', 'constnm', 'productnm', 'equpnm', 'pernm',
  'plandate', 'recedate', 'outdate', 'compdate', 'misdate', 'recenum', 'outnum',
];

function rowLabel(row: Record<string, unknown>): string {
  const parts = ROW_NAME_KEYS
    .map((key) => String(row[key] ?? '').trim())
    .filter(Boolean);
  if (parts.length) return parts.join(' ');
  // 아는 이름 칸이 하나도 없으면 **사람이 읽을 만한 값**을 앞에서 몇 개 고른다.
  // 코드만 든 칸(`00456`)이나 껍데기(`custcd`)는 뺀다.
  const readable = Object.entries(row)
    .filter(([key, value]) => !ROW_BOILERPLATE.has(key) && String(value ?? '').trim())
    .map(([, value]) => String(value).trim())
    .filter((value) => value.length <= 30)
    .slice(0, 3);
  return readable.join(' ') || '(이름 없는 행)';
}

/**
 * 사람이 읽을 수 있는 값인가.
 *
 * `001` 같은 짧은 숫자는 코드다 — 화면에 이름이 따로 있고 사무직은 그 코드를 본 적이
 * 없다. 날짜(`20260827`)는 숫자지만 읽힌다.
 */
function readableValue(value: string): boolean {
  return Boolean(value) && !/^[0-9]{1,4}$/.test(value.trim());
}

/**
 * 내밀 이름들. **같은 글자가 둘이면 가르는 값을 덧붙인다.**
 *
 * 한 거래처의 미수 세 건이 모두 `덕봉빌딩(두암정철)` 로 나왔다. 사용자가 무엇을 고르든
 * 같은 글자라 다음 턴에서 또 갈리지 않는다 — 고를 수 없는 질문이다.
 */
function rowLabels(rows: Record<string, unknown>[]): string[] {
  const base = rows.map(rowLabel);
  return base.map((label, index) => {
    if (base.filter((other) => other === label).length === 1) return label;
    const twins = rows.filter((_, other) => other !== index && base[other] === label);
    const row = rows[index] ?? {};
    const apart = Object.entries(row)
      .filter(([key, value]) => !ROW_BOILERPLATE.has(key)
        && String(value ?? '').trim()
        && !label.includes(String(value)))
      .filter(([key, value]) => twins.every((other) => String(other[key] ?? '') !== String(value)));
    // **코드로 가르면 사람은 못 고른다.** 고장처리결과의 두 행이
    // `… 20260813 006` 과 `… 20260827 001` 로 나왔다 — 006 이 무엇인지 화면 어디에도
    // 없다. 같은 행에 이름 칸(`contnm`)이 있으면 그것도 후보이므로, 코드가 아닌 값을
    // 앞세우는 것만으로 이름이 뽑힌다.
    const shown = apart.map(([, value]) => String(value).trim()).find(readableValue);
    if (shown) return `${label} ${shown}`;
    const [, raw] = apart[0] ?? [];
    return raw === undefined ? label : `${label} ${raw}`;
  });
}

/**
 * 초안 칸을 채우는 순서. **깃발 먼저, 금액 나중.**
 *
 * 이 앱의 체크칸은 켜고 끌 때 짝이 되는 금액칸을 비운다(`hamt: !value && ''`).
 * 금액을 먼저 넣으면 뒤이어 켜는 깃발이 그 값을 지우고, 화면은 `입금할 금액을
 * 확인하십시요` 로 막는다 — 계획이 낸 순서에 기대면 실행마다 달라진다.
 */
function orderedDraft(fields: Record<string, string>): Record<string, string> {
  const rank = (key: string): number => {
    if (/chk$/i.test(key)) return 0;
    if (/amt$/i.test(key)) return 2;
    return 1;
  };
  return Object.fromEntries(Object.entries(fields)
    .sort(([left], [right]) => rank(left) - rank(right)));
}

/**
 * 창 안 표의 이 행이 **준비가 짚은 그 행**인가. 코드는 글자가 같아야 하고, 이름(`…nm`)은
 * 앞이 같으면 된다. 그 표에 없는 칸은 따지지 않는다 — 목록과 창이 싣는 칸이 다르다.
 */
function sameThing(row: Record<string, unknown>, match: Record<string, string>): boolean {
  return Object.entries(match).every(([key, value]) => !Object.prototype.hasOwnProperty.call(row, key)
    || String(row[key] ?? '') === value
    || (/nm$/i.test(key) && sameName(String(row[key] ?? ''), value)));
}

/** 그 표의 지금 행들. */
function rowsOf(stateKey: string): Record<string, unknown>[] {
  const rows = bridge().pageState()?.[stateKey];
  return Array.isArray(rows) ? rows as Record<string, unknown>[] : [];
}

/**
 * **신규에 서버가 코드를 안 주는 화면.** 사람은 목록에서 가장 큰 번호 다음을 적는다.
 *
 * 설치현장등록은 신규를 눌러도 현장코드가 비어 `필수입력확인: 현장코드를 입력하십시요` 로 저장이 막혔다
 * (쓰기 벤치에서 끝까지 남은 실패). 설치형 ERP 는 다섯 자리 숫자 최댓값 + 1 을 자동으로 넣는다 — 웹의 빠진
 * 동작이라 우선 우리가 넣는다(사용자 2026-10-14). 보수현장과 번호를 같이 쓰므로 목록에 없는 번호와 겹치면
 * 화면이 `이미 부여되었습니다` 로 거절한다 — 그때는 다음 번호로 다시 저장한다(`commit`).
 */
const NEXT_CODE: Record<string, { field: string; label: string; listKey: string; digits: number }> = {
  '/w_tb_e601_sulchi': { field: 'actcd', label: '현장코드', listKey: 'data', digits: 5 },
};
/** 다시 고를 번호의 상한. 그 너머는 사람에게 묻는다. */
const NEXT_CODE_TRIES = 10;

/** 목록의 `digits` 자리 숫자 코드 중 가장 큰 것 + 1. 그런 코드가 하나도 없으면 null — 모르는 꼴을 지어내지 않는다. */
export function nextCodeOf(rows: Record<string, unknown>[], field: string, digits: number): string | null {
  const shape = new RegExp(`^[0-9]{${digits}}$`);
  let top = -1;
  for (const row of rows) {
    if (String(row.new ?? '') === '1') continue;
    const code = String(row[field] ?? '').trim();
    if (shape.test(code)) top = Math.max(top, Number(code));
  }
  if (top < 0 || top + 1 >= 10 ** digits) return null;
  return String(top + 1).padStart(digits, '0');
}

/**
 * `then` 으로 고른 안쪽 줄을 화면이 담아 둔 상태 이름(보수현장 `unitFocused`). 호기 칸(승강기번호)은 현장 상세가
 * 아니라 거기 있다 — 현장 상세만 보면 넣은 값이 「반영되지 않았다」였다(2026-10-14). 준비마다 비운다.
 */
let innerRecordKey: string | null = null;
/** 그 안쪽 줄을 가리는 값(호기명·코드). 누르기 직전 그 줄이 아직 잡혀 있나 볼 때 쓴다. */
let innerIdentity: Record<string, string> | null = null;

/** 고른 안쪽 줄이 아직 화면에 잡혀 있나. */
function innerHolds(): boolean {
  if (!innerRecordKey || !innerIdentity) return false;
  const record = bridge().pageState()?.[innerRecordKey];
  if (!record || typeof record !== 'object' || Array.isArray(record)) return false;
  return Object.entries(innerIdentity).every(([key, value]) => String((record as Record<string, unknown>)[key] ?? '') === value);
}

/** 고른 안쪽 줄을 담은 상태 객체 — 그 줄의 값을 다 가진 배열 아닌 객체. */
function innerRecordOf(stateKey: string, wanted: Record<string, string>): string | null {
  const state = bridge().pageState() ?? {};
  for (const [key, value] of Object.entries(state)) {
    if (key === stateKey || !value || typeof value !== 'object' || Array.isArray(value)) continue;
    const record = value as Record<string, unknown>;
    if (Object.entries(wanted).every(([name, one]) => String(record[name] ?? '') === one)) return key;
  }
  return null;
}

function draftState(): Record<string, unknown> | null {
  const detail = detailState();
  const inner = innerRecordKey ? bridge().pageState()?.[innerRecordKey] : undefined;
  if (!inner || typeof inner !== 'object' || Array.isArray(inner)) return detail;
  return { ...(detail ?? {}), ...(inner as Record<string, unknown>) };
}

function draftMismatches(fields: Record<string, string>, pairs?: Record<string, string>): string[] {
  const detail = draftState();
  if (!detail || typeof detail !== 'object') return [];
  const record = detail as Record<string, unknown>;
  return Object.entries(fields)
    .filter(([key, wanted]) => {
      // **빈 값을 요구한 것은 아무것도 요구하지 않은 것이다.** 계획이 모르는 칸을
      // `""` 로 채워 보내는 일이 잦은데(고장처리결과의 부위·원인 코드), 그것을 판정하면
      // 팝업 칸에서 늘 실패한다 — `초안 입력이 화면 상태에 반영되지 않았습니다: gregicd`
      // 로 완료 처리가 통째로 막혔다.
      if (!String(wanted ?? '').trim()) return false;
      const anchor = findDraftField(key);
      if (!anchor || !inputOf(anchor) || !Object.prototype.hasOwnProperty.call(record, key)) return false;
      // 검색 팝업 칸은 이름으로 요청하고 화면에는 **코드**가 들어간다(`광주광산경찰서`
      // → `00176`). 코드끼리 비교할 수는 없지만, 짝이 되는 이름 칸이 있으면 그것으로
      // 맞는지 잴 수 있다 — 팝업이 엉뚱한 행을 확정한 것을 그렇게 잡는다.
      if (hasSearchPopup(anchor)) {
        const filled = String(record[key] ?? '').trim();
        // **등록되지 않은 현장은 코드가 없다.** 이름만 적고 코드는 비운 채 저장하는
        // 화면이 있다(출장보고서). 화면이 스스로 코드를 비우므로 코드 칸이 비었다는
        // 이유로 실패라 하면 미등록 현장 출장은 한 건도 못 쓴다 — 짝 이름 칸이 들은
        // 대로 들어가 있으면 들어간 것이다.
        if (!filled) {
          const only = pairedName(key, record, pairs);
          return only === null ? true : similarity(only, wanted) < 0.8;
        }
        // 계획이 **코드 자체**를 냈고 화면에 그 코드가 들어가 있으면 맞은 것이다.
        // 이름으로만 재던 동안, 이미 본인이 들어가 있는 처리자(`perid=00456`)를 그대로
        // 다시 낸 계획이 `조태호` 와 `00456` 을 견주다 실패로 뒤집혔다.
        if (normalizedDraftValue(filled) === normalizedDraftValue(wanted)) {
          // 다만 **코드 칸에 남은 생 글자**와 코드는 다르다. `민원구분` 에 `소음` 을
          // 밀어 넣으면 값은 그대로 남고 짝 이름(`civilnm`)은 빈 채로 저장된다.
          const only = pairedName(key, record, pairs);
          const codeish = /^[0-9a-z_-]+$/i.test(filled);
          if (codeish || only === null) return false;
          // 짝 이름이 **차 있어도** 안심할 수 없다. 고장부위에 `로프` 를 넣었더니
          // 코드 칸에는 `로프` 가 그대로 남고 이름 칸에는 `휠체어리프트` 가 들어왔다 —
          // 팝업이 엉뚱한 것을 고른 것이다. 들은 말과 다르면 고른 것이 아니다.
          return similarity(only, wanted) < 0.8;
        }
        const named = pairedName(key, record, pairs);
        // 사람은 `덕봉빌딩` 이라 말하고 화면은 `덕봉빌딩(두암정철)` 을 넣는다. 글자가
        // 똑같기를 요구하면 **팝업이 제대로 고른 뒤에** 실패로 뒤집힌다.
        return named === null ? false : similarity(named, wanted) < 0.8;
      }
      // 코드와 짝인 이름 칸은 화면이 스스로 채운다. 사람은 `덕봉빌딩` 이라 말하고
      // 화면은 `덕봉빌딩(두암정철)` 을 넣는다 — 글자가 같기를 요구하면 잘 잡힌 코드를
      // 지우고 말한 대로 다시 적게 된다.
      if (pairedCode(key, fields)) {
        const shown = String(record[key] ?? '').trim();
        return !shown || similarity(shown, wanted) < 0.8;
      }
      return normalizedDraftValue(record[key]) !== normalizedDraftValue(wanted);
    })
    .map(([key]) => key);
}

/**
 * 요청한 칸 중 **화면이 실제로 들고 있는 것**.
 *
 * 「어긋난 것을 뺀 나머지」로 세면 안 된다 — 초안이 아직 열리지도 않았을 때는
 * 어긋난 것도 없어서 **전부 들어갔다고 세어진다.** 고장처리결과에서 행을 고르다
 * 죽었는데 `완료일자·처리결과·고장요인까지 넣었습니다` 가 그대로 나갔고, 화면은
 * 모든 칸이 비어 있었다. 값이 있는지를 직접 본다.
 */
function draftApplied(fields: Record<string, string>, pairs?: Record<string, string>): string[] {
  const detail = draftState();
  if (!detail || typeof detail !== 'object') return [];
  const record = detail as Record<string, unknown>;
  const missed = new Set(draftMismatches(fields, pairs));
  return Object.keys(fields).filter((key) => {
    if (missed.has(key)) return false;
    if (!String(fields[key] ?? '').trim()) return false;
    const shown = String(record[key] ?? '').trim();
    if (shown) return true;
    // 팝업 칸은 코드가 비어도 짝 이름으로 들어간 것일 수 있다(미등록 현장).
    const only = pairedName(key, record, pairs);
    return Boolean(only && only.trim());
  });
}

/**
 * 폼이 **고른 그 문서**를 들고 있는가. 고른 값의 칸 가운데 폼에도 있는 것만 견준다 —
 * 견줄 칸이 하나도 없으면 알 수 없으므로 막지 않는다(지금까지처럼).
 */
function formHolds(wanted: Record<string, string>): boolean {
  const record = draftState();
  if (!record || typeof record !== 'object') return true;
  const form = record as Record<string, unknown>;
  const keys = Object.keys(wanted)
    .filter((key) => String(wanted[key] ?? '').trim() && Object.prototype.hasOwnProperty.call(form, key));
  if (!keys.length) return true;
  return keys.every((key) => normalizedDraftValue(form[key]) === normalizedDraftValue(wanted[key]));
}

/** DOM에만 보이고 React 상태에는 반영되지 않은 가짜 입력을 성공으로 보고하지 않는다. */
function verifyDraftState(fields: Record<string, string>, pairs?: Record<string, string>): void {
  const [missed] = draftMismatches(fields, pairs);
  // 칸 이름 뒤에 조사를 붙이지 않는다. 읽는 쪽이 키를 화면 이름으로 바꿔 넣으므로
  // 받침이 달라져 `현장명가 화면 상태에` 가 된다.
  if (missed) throw new Error(`초안 입력이 화면 상태에 반영되지 않았습니다: ${missed}`);
}

/**
 * 한 질문에 필요한 조회들을 모아 둔다. "작년 동월 대비" 처럼 조건만 다른
 * 조회를 여러 번 하고 결과를 이름으로 꺼내 쓰기 위한 것이다.
 */
/** 지금 떠 있는 창이 제 저장 단추를 들고 있으면 그것. 글자 사이 공백은 무시한다. */
function popupSaveButton(): HTMLElement | null {
  const bare = (value: string) => value.replace(/\s+/g, '');
  const buttons = Array.from(document.querySelectorAll<HTMLElement>('button,div,span,p'))
    .filter((node) => node.children.length === 0 && bare(node.textContent ?? '') === '저장')
    .filter(onScreen)
    // 도구모음의 저장은 화면 전체의 것이다. 창 안에 있는 것만 고른다.
    .filter((node) => !node.closest('[data-el-action]'));
  return buttons.length ? buttons[buttons.length - 1] ?? null : null;
}

/**
 * 목록 밖 값을 **타이프하기 전에** 막는다.
 *
 * 코드와 이름 어느 쪽으로 말해도 받는다 — 계획은 `01` 을 낼 수도 `제어반 (CP)` 를 낼
 * 수도 있고, 둘 다 그 칸의 값이다. 빈 값은 「안 채운다」는 뜻이라 판단하지 않는다.
 * 그 칸의 목록을 모르면(실체표거나 못 받았거나) 지나간다 — 모르는 것을 거절하면
 * 맞는 값도 막는다.
 */
export function rejectDisallowed(
  fields: Record<string, string>,
  allowed: Record<string, string[]> | undefined,
): void {
  if (!allowed) return;
  for (const [key, value] of Object.entries(fields)) {
    const choices = allowed[key];
    if (!choices?.length || !value) continue;
    const wanted = value.trim();
    if (choices.some((choice) => choice.trim() === wanted)) continue;
    // 이름이 겹치면 팝업은 가르는 칸을 붙여 내민다(`본사 03`, popup.ts `labelOf`). 그 단추를
    // 고른 값은 목록의 이름 + 꼬리다 — 어느 행인지는 팝업이 가린다.
    if (choices.some((choice) => /[^0-9A-Za-z_-]/.test(choice.trim()) && wanted.startsWith(`${choice.trim()} `))) continue;
    // 읽는 사람은 회사 사무직이다. 칸 이름(`gregicd`)이 아니라 고를 수 있는 값을 보여 준다.
    //
    // **코드는 보이지 않는다.** 받는 값은 코드와 이름 둘 다지만, 사람에게 내밀 때
    // `01, 회장, 02, 대표` 라고 하면 그 숫자가 무엇인지 다시 묻는다(실측 2026-09-01).
    // 이름이 하나도 없는 목록이면 있는 것을 그대로 보인다 — 아무것도 안 보이는 것보다 낫다.
    const readable = choices.filter((one) => !/^[0-9A-Za-z_-]{1,20}$/.test(one.trim()));
    const shown = readable.length ? readable : choices;
    throw new Error(`${wanted} 은(는) 이 칸에서 고를 수 없습니다. 고를 수 있는 것은 ${
      shown.slice(0, 12).join(', ')
    }${shown.length > 12 ? ` 외 ${shown.length - 12}가지` : ''} 입니다.`);
  }
}

/**
 * 첨부 상자를 품은 탭의 이름.
 *
 * `첨부` 가 든 이름을 먼저 본다 — `파일` 만으로 찾으면 `파일관리`·`첨부파일목록` 같은
 * 다른 탭이 걸린다. 없으면 없다고 한다. 아무 탭이나 누르면 사람이 보던 자리가 바뀐다.
 */
function attachTabTitle(): string | null {
  const titles = Array.from(document.querySelectorAll<HTMLElement>('[data-el-tab]'))
    .map((one) => (one.getAttribute('data-el-tab') ?? '').trim())
    .filter(Boolean);
  return titles.find((one) => one.includes('첨부')) ?? null;
}

/**
 * **요약 값은 행보다 늦게 온다.** 행이 있는데 요약이 전부 0이나 빈 값이면 잠깐 더 본다.
 *
 * 고장처리현황은 모든 페이지를 다 받은 뒤에야 `setState({ rececnt_tot, … })` 로 요약을
 * 채운다. 그 전까지는 초기값 `'0'` 이다. 행이 자리 잡은 순간 읽었더니 「접수 0건 ·
 * 처리 0건」을 가져갔고, 답은 그 0과 행 1,104건을 나란히 말했다(실제 테넌트, 2026-09-23).
 *
 * 정말 0인 화면도 있으므로 오래 붙잡지 않는다 — 최대 1.2초, 마감 안에서만.
 */
const LATE_TOTALS_MS = 1200;

async function lateTotals(keys: string[], rows: number, deadline: number): Promise<Record<string, unknown>> {
  let totals = readScalars(keys);
  const blank = (value: unknown) => value === undefined || value === null || /^[0\s,.]*$/.test(String(value));
  const until = Math.min(deadline, Date.now() + LATE_TOTALS_MS);
  while (rows > 0 && keys.length && Object.values(totals).every(blank) && Date.now() < until) {
    // eslint-disable-next-line no-await-in-loop
    await sleep(POLL_MS);
    totals = readScalars(keys);
  }
  return totals;
}

/**
 * **화면이 멎을 때까지 기다린다.** 바쁨이 `QUIET_MS` 동안 **계속** 꺼져 있고 그동안 표도
 * 안 바뀌어야 멎은 것이다. 「바쁨이 꺼졌다」 한 번으로는 모자라다 — 여러 페이지로 받는
 * 화면은 페이지 사이에 바쁨이 잠깐 꺼진다.
 */
async function untilQuiet(rowsStateKey: string | undefined, deadline: number): Promise<boolean> {
  let calm = 0;
  let last = rowsStateKey ? bridge().pageState()?.[rowsStateKey] : undefined;
  while (calm < QUIET_MS && Date.now() < deadline) {
    // eslint-disable-next-line no-await-in-loop
    await sleep(POLL_MS);
    const now = rowsStateKey ? bridge().pageState()?.[rowsStateKey] : undefined;
    if ((bridge().busy?.() ?? false) || now !== last) {
      calm = 0;
      last = now;
    } else {
      calm += POLL_MS;
    }
  }
  // 마감이 짧아 다 못 센 것과 화면이 아직 일하는 것은 다르다. 바쁜 채로 끝났을 때만 거짓이다.
  return calm >= QUIET_MS || !(bridge().busy?.() ?? false);
}

/**
 * 지금 화면에 보이는 목록 표의 상태 키(`elGrid`). 없으면 없다 — 그때 `untilQuiet` 는
 * 바쁨만 본다.
 */
/**
 * 앞 절반과 뒤 절반이 똑같은 목록. 화면이 같은 응답을 두 번 이어 붙인 모양이다.
 * 정말로 같은 행이 같은 순서로 두 번 이어 나오는 자료는 없다고 본다.
 */
function stacked(rows: Record<string, unknown>[]): boolean {
  if (rows.length < 2 || rows.length % 2) return false;
  const half = rows.length / 2;
  for (let at = 0; at < half; at += 1) {
    if (fingerprint(rows[at], at) !== fingerprint(rows[at + half], at + half)) return false;
  }
  return true;
}

/** 행의 지문. 못 뜨는 행(순환 참조)은 제 자리 번호로 — 어느 것과도 같지 않게 한다. */
function fingerprint(row: unknown, at: number): string {
  try {
    return JSON.stringify(row) ?? `\u0000${at}`;
  } catch {
    return `\u0000${at}`;
  }
}

function arrayAt(key: string): Record<string, unknown>[] {
  const rows = bridge().pageState()?.[key];
  return Array.isArray(rows) ? rows as Record<string, unknown>[] : [];
}

/**
 * **두 조회가 겹쳐 붙은 목록인가.** 앞뒤 절반이 같거나, 바닥줄 수보다 많은데 같은 행이 되풀이된다.
 *
 * 바닥줄 수보다 많기만 한 것은 겹친 것이 아니다 — 예금관리는 `신규` 가 저장 전 줄을 목록
 * 끝에 붙이고, 바닥줄 수는 받은 것 그대로다. 그것을 겹침으로 보고 다시 조회하면 사람이
 * 넣던 줄이 사라진다. 그래서 되풀이된 행이 있을 때만 겹침으로 본다.
 */
function doubledList(key: string): boolean {
  const rows = arrayAt(key);
  if (stacked(rows)) return true;
  const total = listTotal(key);
  if (total === null || rows.length <= total) return false;
  return new Set(rows.map(fingerprint)).size < rows.length;
}

function visibleGridKey(): string | undefined {
  return onScreenAll('[data-el-grid]')[0]?.getAttribute('data-el-grid') ?? undefined;
}

export class Session {
  private readonly retrieved: Record<string, Retrieved> = {};

  /**
   * 방금 이 세션이 채운 초안. 저장 판정에 쓴다.
   *
   * 저장 뒤에도 미저장 행이 남는 화면이 있다 — 그것이 **우리 문서인지** 화면이 새로
   * 깐 뼈대인지는 우리가 넣은 값이 거기 남아 있는지로만 갈린다.
   */
  private lastDraft?: { route: string; fields: Record<string, string>; pairs?: Record<string, string> };
  /** 방금 이 세션이 표에 넣은 행. 헤더 칸이 없는 수정의 저장 판정에 쓴다. */
  private lastRows?: { route: string; items: Record<string, string>[] };
  /** 준비가 짚어 두고 확정으로 미룬 칸. 확정에서 한 번만 쓴다. */
  private deferred?: {
    route: string; stateKey: string; match: Record<string, string>; cells: Record<string, string>;
  };
  /** 준비가 창을 열고 줄을 골라 둔 일. 확정에서 창 단추를 한 번만 누른다. */
  private windowPending?: WindowPending;
  /** 이 준비가 지어 넣은 코드(`NEXT_CODE`). 확정이 `이미 부여` 를 들으면 다음 번호로 다시 저장한다. */
  private autoCode?: { route: string; field: string; label: string; digits: number; tries: number };
  /**
   * **계획을 기다리는 동안 미리 연 화면.** 열고 그 화면의 자체 조회가 멎을 때까지가 한 일이다. 다른 일은 모두 이것이
   * 끝난 뒤에 시작한다 — 반쯤 열린 화면에서 신규를 누르면 늦게 온 목록이 새 행을 밀어낸다(매출등록, 2026-09-25).
   */
  private warming?: Promise<void>;
  private refreshedBySiteSwitch: { route: string | null; state: Record<string, unknown> | null } | null = null;

  /**
   * **계획이 고를 것 같은 화면을 미리 연다**(2026-10-14, 계획 8초 동안 화면 열기·자체 조회를 숨긴다).
   * 기다리지 않고 돌아온다. 화면을 여는 것은 사람이 메뉴를 누르는 것과 같아 아무것도 바꾸지 않는다. 계획이 다른
   * 화면을 고르면 그 화면으로 가면 그만이다. 이미 그 화면이면 아무것도 안 한다.
   */
  warm(routePath: string, timeoutMs = 20000): void {
    if (this.warming || bridge().currentPath() === routePath) return;
    this.warming = (async () => {
      await openScreen(routePath);
      await untilQuiet(visibleGridKey(), Date.now() + timeoutMs);
    })().catch(() => {});
  }

  /** 미리 열던 화면이 다 열리기를 기다린다. 모든 일의 첫 줄이다. */
  private async settleWarm(): Promise<void> {
    const warming = this.warming;
    if (warming) {
      await warming;
      this.warming = undefined;
    }
    // 일을 시작하기 전에 알림 창부터 닫는다 — 로그인 때 뜬 계약만료현장알림이 화면을 가린 채 남아 있었다.
    closeInfoModals();
  }

  async switchBusinessSite(code: string, timeoutMs = 5000): Promise<void> {
    await this.settleWarm();
    const change = bridge().switchBusinessSite;
    if (!change) throw new Error('이 앱은 우측 상단 사업장 전환을 지원하지 않는다');
    if (onBusinessSite(code)) {
      this.refreshedBySiteSwitch = null;
      return;
    }
    const before = { ...(bridge().pageState() ?? {}) };
    if (!await change(code)) throw new Error(`사업장 ${code} 로 전환하지 못했다`);
    const deadline = Date.now() + timeoutMs;
    let switched = false;
    let sawBusy = false;
    const refreshDeadline = Math.min(deadline, Date.now() + BUSY_GRACE_MS);
    while (Date.now() < deadline) {
      switched ||= onBusinessSite(code);
      sawBusy ||= bridge().busy?.() ?? false;
      const changed = changedArrays(before, bridge().pageState()).length > 0;
      if (switched && (sawBusy || changed) && !(bridge().busy?.() ?? false)) {
        // 자동 조회가 표를 비운 직후 읽지 않도록 한 번 더 안정화한다.
        await sleep(SETTLE_MS);
        if (!(bridge().busy?.() ?? false)) {
          this.refreshedBySiteSwitch = {
            route: bridge().currentPath(), state: bridge().pageState(),
          };
          return;
        }
      }
      if (switched && Date.now() >= refreshDeadline) {
        this.refreshedBySiteSwitch = null;
        return;
      }
      await sleep(POLL_MS);
    }
    throw new Error(`사업장 ${code} 전환이 ${timeoutMs}ms 안에 반영되지 않았다`);
  }

  /**
   * 입력이 부른 서버 왕복이 끝날 때까지 기다린다.
   *
   * 마지막 칸의 요청은 blur 뒤에 시작하므로 아직 바쁘지 않을 수 있다. 바빠지기를
   * 잠깐 기다려 보고, 바빠졌으면 끝날 때까지 기다린다. 바쁨을 모르는 앱은 짧게 쉰다.
   */
  /**
   * 고른 문서 안의 표에서 한 줄을 더 고른다(`InnerRow`). 표는 문서를 고른 뒤 늦게 찬다 — 찰 때까지 본다.
   * 사람은 「1-1호기」라 하고 표에는 「1-1」이 있다. 못 찾으면 표에 있는 것을 내민다(`OptionNotFound`).
   */
  private async focusInner(inner: InnerRow, timeoutMs: number): Promise<void> {
    const focus = bridge().focusRow;
    if (!focus) throw new Error('이 앱은 목록의 행을 고르는 조작을 지원하지 않는다');
    const until = Date.now() + Math.min(timeoutMs, 10000);
    while ((!rowsOf(inner.stateKey).length || (bridge().busy?.() ?? false)) && Date.now() < until) {
      // eslint-disable-next-line no-await-in-loop
      await sleep(POLL_MS);
    }
    // **느슨하게 맞추지 않는다.** 이름 닮음(`sameName`)으로는 「1-2」가 「1-1」과 같아, 덕봉빌딩에 없는 1-2호기를
    // 말했는데 1-1호기에 승강기번호를 저장했다(2026-10-14). 공백·「호기」만 떼고 같거나, 그것으로 시작하는 줄 하나.
    const plain = (value: unknown) => String(value ?? '').replace(/\s+/g, '').replace(/호기$/, '');
    const keys = Object.keys(inner.match);
    const rows = rowsOf(inner.stateKey);
    const same = rows.filter((row) => keys.every((key) => plain(row[key]) === plain(inner.match[key])));
    const hits = same.length ? same
      : rows.filter((row) => keys.every((key) => plain(inner.match[key]) && plain(row[key]).startsWith(plain(inner.match[key]))));
    if (hits.length !== 1) {
      throw new OptionNotFound(Object.values(inner.match).join(' '), rowLabels((hits.length ? hits : rows).slice(0, 10)));
    }
    const hit = hits[0]!;
    const wanted = Object.fromEntries(keys.map((key) => [key, String(hit[key] ?? '')]));
    // 그 줄을 가리는 값 — 말한 칸에 코드 칸(`equpcd`·`actcd`)을 더한다. 이름만으로는 다른 현장의 같은 호기명과 같다.
    const identity = {
      ...wanted,
      ...Object.fromEntries(Object.entries(hit)
        .filter(([key, value]) => /cd$/.test(key) && typeof value !== 'object' && String(value ?? '').trim())
        .map(([key, value]) => [key, String(value)])),
    };
    // **고른 줄이 화면에 잡혔는지 본다.** 앱은 호기 표를 채운 뒤 늦게 첫 줄로 포커스를 되돌린다 — 1-2호기를 골랐는데
    // 승강기번호가 1-1호기에 들어갔다(2026-10-14). 잡힐 때까지 다시 고르고, 끝내 안 잡히면 아무것도 쓰지 않는다.
    let held: string | null = null;
    for (let attempt = 0; attempt < 4 && !held; attempt += 1) {
      // eslint-disable-next-line no-await-in-loop
      if (!await focus(inner.stateKey, wanted)) {
        throw new Error(`${Object.values(inner.match).join(' ')} 을(를) 목록에서 고르지 못했다`);
      }
      // eslint-disable-next-line no-await-in-loop
      await this.settleDraft(timeoutMs);
      // eslint-disable-next-line no-await-in-loop
      await sleep(SETTLE_MS);
      held = innerRecordOf(inner.stateKey, identity);
    }
    if (!held) {
      throw new Error(`${Object.values(inner.match).join(' ')} 을(를) 골랐는데 화면이 다른 줄을 잡았다. 아무것도 고치지 않았다`);
    }
    innerRecordKey = held;
    innerIdentity = identity;
  }

  private async settleDraft(timeoutMs: number): Promise<void> {
    const isBusy = bridge().busy;
    if (!isBusy) { await sleep(SETTLE_MS); return; }
    const deadline = Date.now() + timeoutMs;
    const grace = Date.now() + BUSY_GRACE_MS;
    while (!isBusy() && Date.now() < grace) await sleep(POLL_MS);
    while (isBusy() && Date.now() < deadline) await sleep(POLL_MS);
    // 응답이 상태에 반영되는 것은 다음 렌더다. 그것까지 보고 판단한다.
    await sleep(SETTLE_MS);
  }

  /** 신규 화면을 열고 값만 채운다. 저장·삭제·결재는 이 경로에 없다. */
  /**
   * 화면의 저장·삭제를 실제로 누른다. **Auto 가 켜졌을 때만.**
   *
   * 켜졌는지는 여기서 판단하지 않는다 — 사용자가 패널에서 체크한 값을 그대로 받아
   * 드라이버 목록(`runAutoAction`)에 넘긴다. 앱이 "정말 하시겠습니까" 를 띄우면 그
   * 문구를 승인하고 무엇을 승인했는지 돌려준다.
   */
  async commit(request: CommitRequest, timeoutMs = 15000): Promise<{
    notices: string[]; saved: boolean; unconfirmed?: boolean; queued?: QueuedJob[];
    assigned?: { label: string; value: string };
  }> {
    await this.settleWarm();
    // **준비가 미뤄 둔 칸이 있으면 그것이 이 화면의 저장이다.** 점검계획에는 저장 단추가
    // 없다 — 날짜 칸에서 날을 고르는 것이 곧 서버 반영이다.
    const deferred = this.deferred;
    if (deferred && deferred.route === request.routePath && (request.action ?? 'save') === 'save') {
      return this.applyDeferred(deferred, request.auto, timeoutMs);
    }
    // **창에서 골라 둔 일이면 창의 단추가 이 화면의 저장이다.** 도구모음의 저장은 목록을 저장할
    // 뿐 입금처리를 하지 않는다. 한 번만 쓴다 — 누른 뒤 무엇이 됐는지 모르는 채로 또 누르지 않는다.
    const windowed = this.windowPending;
    if (windowed && windowed.route === request.routePath && (request.action ?? 'save') === 'save') {
      if (!request.auto) throw new Error('save 는 Auto 모드가 켜져 있을 때만 할 수 있다');
      this.windowPending = undefined;
      return applyWindowPick(windowed, request.auto, timeoutMs);
    }
    if (bridge().currentPath() !== request.routePath) {
      await openScreen(request.routePath);
    }
    // **저장을 누르기 전에 초안이 그대로인지 본다.**
    //
    // 업무일지는 업무일자를 바꾸면 그날치를 서버에서 다시 불러온다. 그날 일지가
    // 없으면 화면이 **앞서 고른 날로 되돌아가고** 우리가 넣은 값은 사라진다. 그런데
    // 저장은 되돌아간 그 행을 저장하므로 화면은 `서버에 잘 저장했습니다` 라 답한다 —
    // 초안 검증은 되돌기 **전에** 지나갔고, 미저장 행도 남지 않아 아무도 못 잡았다.
    // 실측: `오늘 업무일지 써줘` 가 저장됐다고 답했는데 그날 일지는 없었다.
    if (request.action !== 'delete' && this.lastDraft?.route === request.routePath) {
      const reverted = draftMismatches(this.lastDraft.fields, this.lastDraft.pairs);
      if (reverted.length) {
        // 한 번은 다시 넣어 본다 — 화면이 잠깐 다녀온 것일 수 있다.
        const again = Object.fromEntries(reverted
          .map((key) => [key, this.lastDraft!.fields[key] ?? '']));
        await fillConditions(orderedDraft(again), undefined, { draft: true }).catch(() => {});
        await this.settleDraft(timeoutMs);
        const still = draftMismatches(this.lastDraft.fields, this.lastDraft.pairs);
        if (still.length) {
          return {
            notices: [`화면이 넣은 값을 되돌렸습니다: ${still.join(', ')}`],
            saved: false,
          };
        }
      }
    }
    // **저장 판정은 문구가 아니라 생긴 것으로 한다.** 저장 직전에 결과 표(있으면)의
    // 미저장 아닌(`new!=='1'`) 행 식별자를 배열마다 찍어 둔다. 저장 뒤 어느 배열에든
    // 저장 전에 없던 non-new 행이 생겼으면 저장된 것이다 — 통지가 경고든 무엇이든.
    // 고장접수가 「호기를 확인하십시요」를 띄우며 저장하는데 그것을 거절로 읽어 되짜서
    // 하루에 8건이 쌓였다. `commit` 은 결과 표 이름을 모르므로 모든 배열을 본다.
    const idsOf = (rows: unknown): Set<string> => {
      const seen = new Set<string>();
      if (!Array.isArray(rows)) return seen;
      for (const row of rows) {
        if (!row || typeof row !== 'object') continue;
        const rec = row as Record<string, unknown>;
        if (String(rec.new ?? '') === '1') continue;
        // 식별자: 값이 있는 *num/*cd/*date/no/id 열을 이어 붙인다. **없으면 건너뛴다.**
        // 인덱스(`#0`, `#1`)로 자리표를 달면 재정렬만으로 그 자리가 「새 행」처럼 보여
        // 저장 안 한 문서를 저장으로 오인한다 — 차이로만 본다는 불변이 자리에 의존하면
        // 깨진다. 식별자 없는 행은 집합에 안 담아 새 행 탐지에서 아예 빠진다(옛 규칙으로).
        const id = Object.entries(rec)
          .filter(([k, v]) => /num$|cd$|date$|no$|id$/i.test(k) && String(v ?? '').trim())
          .map(([, v]) => String(v)).join('');
        if (id) seen.add(id);
      }
      return seen;
    };
    const snapshotArrays = (): Record<string, Set<string>> => {
      const state = bridge().pageState() ?? {};
      const before: Record<string, Set<string>> = {};
      for (const [k, v] of Object.entries(state)) if (Array.isArray(v)) before[k] = idsOf(v);
      return before;
    };
    // 저장을 누르기 **전에** 진행 중인 조회가 있으면 그것이 끝난 목록을 찍는다. 조회
    // 도중에 전 스냅샷을 찍으면 반쪽 목록과 비교하게 되어, 저장 뒤 채워진 행을 「새 행」
    // 으로 오인하거나 반대로 놓친다(Minor 5).
    const preBusy = bridge().busy;
    if (preBusy) {
      const preDeadline = Date.now() + timeoutMs;
      while (preBusy() && Date.now() < preDeadline) {
        // eslint-disable-next-line no-await-in-loop
        await sleep(POLL_MS);
      }
    }
    const beforeArrays = request.action === 'delete' ? null : snapshotArrays();
    const queueBefore = waitQueue();
    let notices = await runAutoAction(request.action ?? 'save', request.auto);
    // **창 안에서 저장하는 화면이 있다.** 스케쥴관리는 그날치 창에서만 쓰고, 화면의
    // 저장은 `기능 없음: 이 화면에서는 저장할 수 없습니다` 로 거절한다. 창이 제 저장을
    // 들고 있으면 그것을 누른다 — 글자 사이가 벌어져 있기도 하다(`저 장`).
    if ((request.action ?? 'save') === 'save' && notices.some((one) => /기능 없음/.test(one))) {
      const inPopup = popupSaveButton();
      if (inPopup) {
        inPopup.dispatchEvent(new MouseEvent('mousedown', { bubbles: true }));
        inPopup.dispatchEvent(new MouseEvent('mouseup', { bubbles: true }));
        inPopup.dispatchEvent(new MouseEvent('click', { bubbles: true }));
        await sleep(SETTLE_MS);
        notices = await dismissNotices(3, 1200);
      }
    }
    const deadline = Date.now() + timeoutMs;
    const isBusy = bridge().busy;
    if (isBusy) {
      while (isBusy() && Date.now() < deadline) {
        // eslint-disable-next-line no-await-in-loop
        await sleep(POLL_MS);
      }
    }
    // 저장했는지는 **문구가 아니라 상태로** 판단한다. 견적서가 `필수입력확인: 견적서명을
    // 입력하십시요` 를 띄우며 거절했는데 답변은 `저장했습니다` 로 나갔다 — 알림을 성공의
    // 증거로 읽었기 때문이다. 미저장 행(`new === '1'`)이 남아 있으면 저장되지 않았다.
    // **헤더가 비고 행만 바뀐 문서도 있다.** `출장보고서에 숙박비 8만원 추가해줘` 는
    // 초안 칸이 하나도 없어 가리킬 값이 없었고, 화면이 남긴 빈 뼈대를 우리 것으로 읽어
    // 잘 저장된 문서를 `저장은 되지 않았습니다` 로 답했다. 그때는 우리가 넣은 **행 값**이
    // 잣대다.
    const fields = this.lastDraft?.route === request.routePath ? this.lastDraft.fields : undefined;
    const rowSpec = this.lastRows?.route === request.routePath ? this.lastRows.items : undefined;
    const pointed = fields && Object.values(fields).some(identifying);
    const mine = pointed || !rowSpec?.length
      ? fields
      : { ...(fields ?? {}), ...Object.assign({}, ...rowSpec) as Record<string, string> };
    // 화면이 거절했다고 말했으면 그것으로 끝이다. 알림을 **성공의 증거로는** 여전히
    // 쓰지 않는다 — 그래서 `저장했습니다` 를 띄우고도 안 저장한 화면에 속지 않는다.
    // 반대 방향은 안전하다: 거절 문구는 저장이 안 됐을 때만 나온다.
    // 저장 뒤 어느 배열에든 **저장 전에 없던 non-new 행**이 생겼는가. 안전상 **차이로만**
    // 본다 — 원래 있던 행은 안 센다. 이것이 참이면 통지가 경고든 무엇이든 저장된 것이다:
    // 고장접수가 「호기를 확인하십시요」를 띄우며 저장하는데 그것을 거절로 읽어 되짜서
    // 하루에 8건이 쌓였다. 새 행은 **성공의 확실한 증거**라 통지를 이긴다.
    const grewByNewRow = (): boolean => {
      if (!beforeArrays) return false;
      const state = bridge().pageState() ?? {};
      for (const [k, before] of Object.entries(beforeArrays)) {
        const now = idsOf((state as Record<string, unknown>)[k]);
        // `now` 는 Set 이다. 앱(elmanpro)은 이 소스를 복사해 `target: es5` + downlevelIteration
        // 없이 검사하므로 Set 직접 순회는 TS2802 로 그쪽 타입검사를 깬다 — `Array.from` 으로 감싼다.
        for (const id of Array.from(now)) if (!before.has(id)) return true;
      }
      return false;
    };
    // 새 행이 안 생겼으면 **옛 규칙**으로 물러난다. 결과 표가 없는 화면(저장이 목록에
    // 즉시 안 뜸)과, 이미 있던 문서의 상세만 고친 수정(마스터 목록에 새 행이 안 생김)이
    // 여기로 온다 — 출장보고서에 숙박비 한 줄 더한 것은 tripList 에 새 행을 안 남긴다.
    // 옛 규칙은 화면이 거절했다고 말한 문구를 **실패 증거로만** 쓴다(성공 증거로는 안 쓴다).
    const refused = notices.some(refusedNotice);
    const savedByOldRule = !refused && unsavedRowKey(mine) === null;
    let grew = grewByNewRow();
    // **고장접수는 성공 통지와 「알림을 전송하시겠습니까?」 대화를 답한 뒤에야 목록을
    // 새로고침한다.** busy 가 걷힌 틈에 판정하면 새로고침 **전** stale 목록을 읽어 새 행을
    // 못 본다 → 옛 규칙 → 「경고」로 saved:false → 런타임이 되짜 중복 저장(R1). 새 행이
    // 없고 옛 규칙마저 실패라 할 때만 한 번 더 settle 하고 **같은 beforeArrays** 와 다시
    // 본다 — 새 기준은 안 찍는다(그러면 원래 있던 행이 새 행처럼 보인다). 성공 판(이미
    // 새 행이 보임)에는 지연을 안 준다.
    if (!grew && !savedByOldRule && request.action !== 'delete') {
      await sleep(SETTLE_MS);
      grew = grewByNewRow();
    }
    const saved = request.action === 'delete'
      ? true
      : grew || savedByOldRule;
    // **옛 규칙만으로 저장이라 했으면 증거가 없다.** 알림도 새 행도 없이 「거절 안 함 + 미저장
    // 행 없음」만으로 저장이라 했다 — 거래처등록이 저장되지 않았는데 「저장했습니다」로
    // 답했다(2026-10-05 벤치, 목록 11,272행 그대로). 실패로 바꾸면 런타임이 다시 눌러 중복
    // 저장한다(고장접수 R1). 그래서 판정은 두고 「확인 못 함」을 달아 답만 바꾸게 한다.
    const unconfirmed = saved && request.action !== 'delete' && !grew && !notices.some(savedNotice);
    // **대기열에 올라갔으면 서버가 받은 것이다.** 국세청 매출처리처럼 저장이 서버 일로 넘어가면 새 행은
    // 나중에 생긴다. 그것을 「저장 못 함」으로 읽으면 런타임이 다시 눌러 같은 일을 두 번 맡긴다.
    const queued = queuedSince(queueBefore);
    if (queued.length) {
      closeWaitQueueWindow();
      return { notices, saved: true, queued };
    }
    // **지어 넣은 코드가 남의 것과 겹쳤으면 다음 번호로 다시 저장한다.** 거절은 저장이 안 됐다는 뜻이라
    // 다시 눌러도 두 벌이 안 생긴다. 사람이 받은 코드가 아니므로 묻지 않는다.
    const auto = this.autoCode;
    if (!saved && !grew && auto && auto.route === request.routePath && this.lastDraft?.route === request.routePath
      && auto.tries < NEXT_CODE_TRIES && notices.some((one) => /이미 부여/.test(one))) {
      auto.tries += 1;
      const code = String(Number(this.lastDraft.fields[auto.field]) + 1).padStart(auto.digits, '0');
      if (code.length === auto.digits) {
        await fillConditions({ [auto.field]: code }, undefined, { draft: true });
        await this.settleDraft(timeoutMs);
        this.lastDraft = { ...this.lastDraft, fields: { ...this.lastDraft.fields, [auto.field]: code } };
        return this.commit(request, timeoutMs);
      }
    }
    const assigned = saved && auto && auto.route === request.routePath && this.lastDraft?.route === request.routePath
      ? { label: auto.label, value: String(this.lastDraft.fields[auto.field] ?? '') }
      : undefined;
    return { notices, saved, ...(unconfirmed ? { unconfirmed: true } : {}), ...(assigned?.value ? { assigned } : {}) };
  }

  /**
   * 화면 고유 버튼을 눌러 앞 문서를 다음 문서로 잇는다.
   *
   * 견적서에서 `자재청구서` 를 누르면 모달이 뜨고 `확인` 이 청구서를 만든다. 액션
   * 목록(`retrieve/new/save…`)에는 없는 길이라 여기서 따로 연다.
   */
  /**
   * **미결함 문서를 결재한다** — 런타임이 사람에게 목록을 보이고 확인받은 문서 번호만 온다.
   * 목록을 다시 조회해 지금 그대로 있는 것만 한 건씩 연다(`approval.ts`). 하나가 막혀도 나머지는 한다.
   */
  async approve(request: {
    appnums: string[];
    action: ApprovalAction;
    remark?: string;
    auto: boolean;
    conditions?: Record<string, string>;
  }, timeoutMs = 20000): Promise<{ results: ApprovalResult[] }> {
    if (!request.auto) throw new Error('결재는 Auto 모드가 켜져 있을 때만 할 수 있다');
    await this.settleWarm();
    await this.retrieve({
      label: `결재전-${Date.now()}`,
      routePath: '/w_tb_e080_list',
      rowsStateKey: 'incompleteBoxList',
      conditions: request.conditions ?? {},
      force: true,
    }, timeoutMs);
    const results: ApprovalResult[] = [];
    for (const appnum of request.appnums) {
      // eslint-disable-next-line no-await-in-loop
      results.push(await approveOne(appnum, request.action, request.remark ?? ''));
    }
    return { results };
  }

  async operate(request: OperateRequest, timeoutMs = 15000): Promise<{ notices: string[]; queued?: QueuedJob[] }> {
    await this.settleWarm();
    if (bridge().currentPath() !== request.routePath) {
      await openScreen(request.routePath);
      await sleep(SETTLE_MS);
    }
    // **누를 행이 화면에 있어야 한다.** 조건을 받았거나 표가 비었으면 먼저 조회한다.
    // 이름은 겹치지 않게 짓는다 — `retrieve` 는 같은 이름을 두 번 쓰면 던진다.
    if (request.row && (request.row.conditions || !readRows(request.row.stateKey).length)) {
      await this.retrieve({
        label: `누르기전-${request.row.stateKey}-${Date.now()}`,
        routePath: request.routePath,
        rowsStateKey: request.row.stateKey,
        conditions: request.row.conditions ?? {},
        force: true,
      }, timeoutMs);
    }
    if (request.list) {
      const { stateKey, conditions, check, checkKey = 'chk', match } = request.list;
      if (conditions) {
        await this.retrieve({
          label: `누르기전-${stateKey}-${Date.now()}`,
          routePath: request.routePath,
          rowsStateKey: stateKey,
          conditions,
          force: true,
        }, timeoutMs);
      }
      if (!readRows(stateKey).length) throw new Error(`${request.button} 을 누를 대상이 없다 — 목록이 비었다`);
      if (check && match && Object.keys(match).length) {
        // **말한 줄만 고른다.** 「OO 계산서 보내줘」에 목록 전부를 켜면 남의 계산서까지 나간다.
        const one = bridge().setRowCheck;
        if (!one) throw new Error(`${request.button} 을 누르기 전에 줄 하나씩 고를 수 없다(구버전 앱)`);
        const squash = (value: unknown) => String(value ?? '').replace(/\s+/g, '');
        const rows = readRows(stateKey);
        const picked = rows.map((row, index) => ({ row, index }))
          .filter(({ row }) => Object.entries(match).every(([key, value]) => squash(row?.[key]).startsWith(squash(value))));
        if (!picked.length) throw new Error(`${request.button} 을 누를 대상이 없다 — 목록이 비었다(${Object.values(match).join(' ')})`);
        for (const { index } of picked) {
          // eslint-disable-next-line no-await-in-loop
          if (!(await one(stateKey, index, checkKey, true))) {
            throw new Error(`${request.button} 을 누르기 전에 목록의 행을 고르지 못했다`);
          }
        }
      } else if (check) {
        const setter = bridge().setCheckedRows;
        if (!setter || !(await setter(stateKey, checkKey, true))) {
          throw new Error(`${request.button} 을 누르기 전에 목록의 행을 고르지 못했다`);
        }
      }
    }
    let ready: (() => boolean) | undefined;
    if (request.focus) {
      const focus = bridge().focusRow;
      if (!focus) throw new Error('이 앱은 목록의 행을 고르는 조작을 지원하지 않는다');
      // 조건을 받았으면 먼저 조회한다 — 지난 견적은 화면 기본 기간(이번 달)에 없다.
      let found = request.focus.conditions ? false : await focus(request.focus.stateKey, request.focus.match);
      if (!found) {
        await this.retrieve({
          label: `누르기전-${request.focus.stateKey}-${Date.now()}`,
          routePath: request.routePath,
          rowsStateKey: request.focus.stateKey,
          conditions: request.focus.conditions ?? {},
          force: true,
        }, timeoutMs);
        // 사람은 짧게 말한다(「덕봉빌딩」) — 목록 값(「덕봉빌딩(두암정철)」)으로 고른다. 여럿이면 고르지 않는다.
        const rows = readRows(request.focus.stateKey);
        const hits = rows.filter((row) => Object.entries(request.focus!.match)
          .every(([key, value]) => String(row?.[key] ?? '').replace(/\s+/g, '').startsWith(value.replace(/\s+/g, ''))));
        if (hits.length > 1) {
          // 이름만 보이면 다 같은 줄이다 — 날짜·이름 칸을 붙여 가릴 수 있게 한다.
          const telling = (row: Record<string, unknown>) => Object.entries(row)
            .filter(([key, value]) => typeof value !== 'object' && String(value ?? '').trim()
              && (key in request.focus!.match || /date$|nm$|name$|title$/i.test(key)))
            .slice(0, 4).map(([, value]) => String(value)).join(' ');
          throw new Error(`${request.button} 을 누를 문서가 ${hits.length}개다 — 어느 것인지 말해 달라: ${hits.slice(0, 5)
            .map(telling).join(', ')}`);
        }
        const exact = hits[0]
          ? Object.fromEntries(Object.keys(request.focus.match).map((key) => [key, String(hits[0]![key] ?? '')]))
          : request.focus.match;
        request = { ...request, focus: { ...request.focus, match: exact } };
        found = await focus(request.focus!.stateKey, exact);
      }
      if (!found) throw new Error(`${request.button} 을 누를 문서를 목록에서 찾지 못했다`);
      // **문서가 다 읽힐 때까지 기다린다.** 행을 고르면 앱은 상세를 빈 새 문서로 비웠다가 서버 응답으로
      // 채운다. 그 사이 누른 상신은 빈 문서(업무일자 오늘·사번 없음)를 저장하고 올려 서버가 「본인이
      // 작성하지 않은 문서」로 막았다(웹소켓 기록, 2026-10-13). 고른 값이 상세에 앉을 때까지 본다.
      // 합친 상세(`detailState`)는 안 된다 — 다른 상세 객체가 고른 값을 먼저 들고 있어 곧바로 참이었다.
      // 화면 문서(`data`)가 있으면 그것을 본다. 앱이 바빠지기 전 틈에 끝나지 않게 바쁨이 시작하기를 먼저 기다린다.
      const match = request.focus!.match;
      const loaded = () => {
        const page = bridge().pageState() ?? {};
        // 견적서는 `data` 가 목록(배열)이고 문서는 `content` 다 — 배열을 문서로 읽으면 늘 「안 잡혔다」였다.
        const own = page.data && typeof page.data === 'object' && !Array.isArray(page.data)
          ? page.data as Record<string, unknown> : null;
        const detail = own ?? detailState();
        if (!detail || !Object.entries(match)
          .every(([key, value]) => !(key in detail) || String(detail[key] ?? '') === value)) return false;
        if (!own) return true;
        if (!Object.keys(match).some((key) => key in own)) return false;
        // **빈 새 문서도 고른 값과 같을 수 있다** — 업무일지 새 문서의 업무일자는 오늘이다. 목록의 그 줄과
        // 화면 문서가 같이 가진 칸(사번·문서번호)이 다 같아야 그 문서를 다시 읽은 것이다.
        const listed = (Array.isArray(page[request.focus!.stateKey]) ? page[request.focus!.stateKey] as Record<string, unknown>[] : [])
          .find((row) => Object.entries(match).every(([key, value]) => String(row?.[key] ?? '') === value));
        if (!listed) return true;
        const shared = Object.keys(listed).filter((key) => key in own && String(listed[key] ?? '').trim()
          && typeof listed[key] !== 'object');
        return shared.every((key) => String(own[key] ?? '').trim() === String(listed[key] ?? '').trim());
      };
      const started = Date.now() + 1000;
      while (!(bridge().busy?.() ?? false) && Date.now() < started) {
        // eslint-disable-next-line no-await-in-loop
        await sleep(POLL_MS);
      }
      const until = Date.now() + 8000;
      while ((!loaded() || (bridge().busy?.() ?? false)) && Date.now() < until) {
        // eslint-disable-next-line no-await-in-loop
        await sleep(POLL_MS);
      }
      await sleep(SETTLE_MS);
      innerRecordKey = null;
      innerIdentity = null;
      if (request.focus!.then) await this.focusInner(request.focus!.then, timeoutMs);
      // 누른 뒤 물음에 답할 때도 같은 문서여야 한다 — 단추가 목록을 다시 읽어 다른 줄을 잡을 수 있다.
      // 안쪽 줄(호기)까지 골랐으면 **그 줄이 잡혀 있는지**로 본다. 호기를 고르면 상세가 호기로 바뀌어 현장 값과
      // 안 맞는다 — 정보센터연동이 「고른 문서를 다시 잡지 못해 아니오」로 멈췄다(2026-10-14).
      ready = request.focus!.then
        ? () => innerHolds() && !(bridge().busy?.() ?? false)
        : () => loaded() && !(bridge().busy?.() ?? false);
    }
    const queueBefore = waitQueue();
    const notices = request.row
      ? (await pressRowButton({ ...request.row, auto: request.auto })).notices
      : await pressNamed(
        request.button, request.auto, request.confirm, request.fields, request.selectKey, ready,
      );
    const deadline = Date.now() + timeoutMs;
    const isBusy = bridge().busy;
    if (isBusy) {
      while (isBusy() && Date.now() < deadline) {
        // eslint-disable-next-line no-await-in-loop
        await sleep(POLL_MS);
      }
    }
    // 서버에 맡긴 일이면 끝을 기다리지 않는다 — 몇 분이 걸린다. 무엇이 올라갔는지만 돌려주고 패널이 지켜본다.
    const queued = queuedSince(queueBefore);
    if (queued.length) closeWaitQueueWindow();
    return { notices, ...(queued.length ? { queued } : {}) };
  }

  /**
   * 메일·문자·카카오톡을 보낸다. **Auto 일 때만.**
   *
   * 화면 액션이 아니라 전역 모달을 거친다 — 채널 버튼을 눌러 창을 띄우고, 앵커로 칸을
   * 채운 뒤 `전송` 을 누른다. 팩스는 첨부가 필수라 여기서 나가지 못한다.
   */
  async send(request: {
    routePath: string; channel: SendChannel; auto: boolean;
    to?: string; subject?: string; body?: string;
    /** 전송 창 앞에 놓인 창이 요구하는 값. 고장접수의 문자유형이 그렇다. */
    fields?: Record<string, string>;
  }): Promise<{ notices: string[] }> {
    await this.settleWarm();
    if (bridge().currentPath() !== request.routePath) {
      await openScreen(request.routePath);
      await sleep(SETTLE_MS);
    }
    const notices = await sendMessage(
      request.channel,
      {
        to: request.to, subject: request.subject, body: request.body, fields: request.fields,
      },
      request.auto,
    );
    return { notices };
  }

  /**
   * 화면을 **보여 주기만** 한다. 조회도 입력도 하지 않는다.
   *
   * 답을 낸 뒤에 딴 화면에 남겨 두지 않으려고 쓴다. 런타임은 이름 확인 같은 일로
   * 조용히 다른 화면에 다녀오는데, 그러고 나면 사람은 묻지도 않은 보수현장현황을
   * 보고 있었다(2026-09-22, 「그 중에 덕봉빌딩 것만」 두 턴짜리 대화에서 실측).
   *
   * 이 ERP 는 화면을 탭으로 물고 있으므로 대개 탭 전환 한 번이다. 이미 그 화면이면
   * 아무것도 하지 않는다.
   */
  async show(routePath: string): Promise<void> {
    await this.settleWarm();
    if (bridge().currentPath() === routePath) return;
    await openScreen(routePath);
  }

  /**
   * 탭을 열고 **그 탭 자료가 도착할 때까지 기다린다.**
   *
   * 기다리는 것이 이 함수의 절반이다. 열자마자 채우면 뒤늦게 온 탭 자료가 방금 넣은
   * 값을 덮는데, 드라이버는 그것을 모르고 성공이라 답한다 — 인사등록 가족 탭에서 봤다.
   */
  async openTab(request: { routePath: string; tab: string }): Promise<void> {
    await this.settleWarm();
    // 앞 걸음이 남긴 확인 대화가 붙어 있으면 아무것도 못 누른다.
    clearPendingConfirms();
    if (bridge().currentPath() !== request.routePath) {
      await openScreen(request.routePath);
      await sleep(SETTLE_MS);
    }
    await pressTab(request.tab);
  }

  /**
   * 사람이 올린 파일을 **지금 화면의 문서**에 붙인다.
   *
   * **화면을 건드리지 않는다.** 문서 번호도 파일도 앱이 안다 — 드라이버가 하는 일은
   * 그 화면을 열어 두는 것뿐이다.
   *
   * 붙일 길이 없으면 **0 을 돌려준다.** 던지지 않는다 — 구버전 앱에서 대화가 죽는다.
   */
  async attach(request: { routePath: string }): Promise<{ attached: number; notices: string[] }> {
    await this.settleWarm();
    const hook = bridge().attach;
    if (!hook) {
      return { attached: 0, notices: ['이 화면에서는 파일을 붙일 수 없습니다.'] };
    }
    if (bridge().currentPath() !== request.routePath) {
      await openScreen(request.routePath);
      await sleep(SETTLE_MS);
    }
    const first = await hook();
    if (first.attached) return first;
    // **첨부 상자는 그 탭이 열려야 화면에 생긴다.** 인사등록은 `첨부파일` 탭이다.
    // 카탈로그의 탭 목록에는 없다 — 표가 없는 탭이라 관찰이 못 잡는다. 그래서 이름으로
    // 찾는다. 한 번만 눌러 보고, 그래도 없으면 첫 대답을 그대로 돌려준다.
    const tab = attachTabTitle();
    if (!tab) return first;
    try {
      await pressTab(tab);
    } catch {
      return first;
    }
    await sleep(SETTLE_MS);
    return hook();
  }

  /**
   * 그 칸이 고를 수 있는 것들. 저장이 막힌 뒤 사람에게 내밀 후보를 얻는다.
   *
   * 미리 담을 수 없는 목록이 있다 — 호기는 현장마다 다르고 고장부위상세는 앞 칸에
   * 매달린다. 그 칸들을 `화면에서 직접 골라 주세요` 로 돌려보내던 자리를 메운다.
   *
   * **팝업 칸만 답하면 절반이 빈다.** 막는 칸의 다수는 콤보이고, 초안 콤보 59개 중
   * 40개가 선택지를 서버에서 받아 온다 — 카탈로그에도 없어서 미리 담을 수 없다.
   * 팝업이 아무것도 못 주면 같은 칸을 콤보로 한 번 더 물어본다. 둘 다 없으면 빈 것이
   * 맞고, 그때는 부르는 쪽이 사람에게 묻는다.
   */
  async options(
    request: { routePath: string; field: string },
  ): Promise<{ options: string[]; reason?: string }> {
    await this.settleWarm();
    if (bridge().currentPath() !== request.routePath) return { options: [] };
    // 팝업 칸은 여기서 직접 연다. `popupOptions` 는 팝업이 안 열려도 이유를 삼키고 `[]`
    // 만 주는데, 저장 뒤 경로가 「고를 게 없다」와 「못 열었다」를 갈라 말하려면 그 이유가
    // 필요하다. 열려서 후보가 나오면 그것을 주고, 끝내 안 열리면 이유를 실어 준다.
    const popup = await this.readPopupOptions(request.field);
    if (popup.options.length) return popup;
    const combo = await comboOptions(request.field, true);
    if (combo.length) return { options: combo };
    // 콤보도 비었다. 팝업이 안 열렸다는 이유(있으면)를 살려 돌려준다.
    return popup;
  }

  /**
   * 팝업 칸의 후보를 직접 읽는다. `popupOptions`(actions.ts) 와 같은 수법이되,
   * `OptionNotFound.reason`(팝업이 안 열렸다)을 삼키지 않고 실어 온다.
   */
  private async readPopupOptions(field: string): Promise<{ options: string[]; reason?: string }> {
    const anchor = findDraftField(field);
    if (!anchor || !hasSearchPopup(anchor)) return { options: [] };
    try {
      await pickFromSearchPopup(anchor, POPUP_PROBE);
      return { options: [] };
    } catch (error) {
      if (error instanceof OptionNotFound) return { options: error.options, reason: error.reason };
      return { options: [] };
    }
  }

  async prepare(request: PrepareRequest, timeoutMs = 15000): Promise<Prepared> {
    await this.settleWarm();
    // 앞 걸음이 남긴 확인 대화를 먼저 치운다. 붙어 있으면 아무것도 못 누른다.
    clearPendingConfirms();
    // 앞 준비가 미뤄 둔 칸은 이 준비로 끝난다. 남겨 두면 다음 확정이 남의 행을 바꾼다.
    this.deferred = undefined;
    this.windowPending = undefined;
    innerRecordKey = null;
    this.autoCode = undefined;
    // 값이 목록 밖이면 화면을 열 이유도 없다. 제일 먼저 본다.
    rejectDisallowed(request.fields, request.allowed);
    const opened = bridge().currentPath() !== request.routePath;
    if (opened) await openScreen(request.routePath);
    // **달력 화면은 그 날을 열어야 쓸 자리가 생긴다.** 스케쥴관리는 헤더 입력칸이
    // 하나도 없고, 날짜 칸을 눌러야 그날치 창이 뜬다.
    if (request.calendarDay) {
      await this.showCalendarMonth(request.calendarDay, timeoutMs);
      await openCalendarDay(request.calendarDay);
    }
    // 이미 있는 문서를 고치는 길. 신규를 누르지 않고 그 행을 잡아 값을 채운다 —
    // 고장완료는 접수가 이미 있고 그 행에 처리결과를 적는 일이다.
    if (request.edit?.window) return this.prepareWindow(request, timeoutMs);
    if (request.edit?.deferCells) return this.prepareDeferred(request, timeoutMs);
    if (request.edit) {
      const cells = request.edit.cells ?? {};
      const editsCells = Object.keys(cells).length > 0;
      // **헤더가 먼저인 화면에서는 행을 짚지 않는다.** 헤더는 고른 행의 문서가 아니다.
      // 앱의 `focusRow` 는 목록이 무엇이든 화면의 `onRowFocusEvent` 를 부르는데, 입금입력에서
      // 그것은 입금이력 표 담당이다 — 미수 행을 넘기자 빈 입금번호로 서버를 불러 180초를
      // 멎었고, 그 사이 미뤄진 「현금」 체크가 금액을 넣은 뒤에 반영돼 금액을 비웠다(2026-09-29).
      const focus = request.edit.fieldsFirst ? undefined : bridge().focusRow;
      // 표의 칸만 고치는 화면은 행을 고를 필요가 없다 — 점검결과등록에는 행 포커스가
      // 아예 없다. 그 화면에서 포커스를 요구하면 아무것도 못 고친다.
      if (!focus && !editsCells) {
        throw new Error('이 앱 버전은 목록에서 문서를 고르는 것을 지원하지 않습니다.');
      }
      await this.loadEditList(request.edit, timeoutMs);
      // 사람은 짧게 말한다 — `덕봉빌딩`, `1-2호기`. 목록의 값은 `덕봉빌딩(두암정철)`,
      // `1-2` 다. 화면의 행 선택은 **글자가 똑같기를** 요구하므로, 여기서 느슨하게 찾아
      // 그 행의 **실제 값**으로 고르게 한다.
      // 「OO 현장 비고에 …」에서 계획이 「OO 현장」까지 이름으로 읽는다 — 목록에는 「OO」뿐이다(2026-10-13 벤치).
      const bare = Object.fromEntries(Object.entries(request.edit.match)
        .map(([key, value]) => [key, String(value ?? '').replace(/\s+현장$/, '')]));
      const wanted = pickedRow(request.edit.rowsStateKey, request.edit.pick)
        ?? matchingRow(request.edit.rowsStateKey, request.edit.match)
        ?? matchingRow(request.edit.rowsStateKey, bare)
        ?? request.edit.match;
      const cellsKey = request.edit.cellsStateKey ?? request.edit.rowsStateKey;
      // **칸을 고칠 표가 고른 문서의 것인지**는 다른 표를 고칠 때만 묻는다. 같은 표면 그 행이 곧 문서다.
      const otherTable = editsCells && cellsKey !== request.edit.rowsStateKey;
      const shownBefore = bridge().pageState()?.[cellsKey];
      const focused = focus
        ? await focus(request.edit.rowsStateKey, wanted)
        : false;
      if (!focused && !editsCells) throw new Error('고칠 문서를 목록에서 찾지 못했습니다.');
      // **다른 표를 고치는데 문서를 못 골랐으면 멈춘다.** 그 표에는 지금 화면이 보이는 다른 문서가 떠
      // 있다 — 일근태등록은 열면 그 달 마지막 날의 직원 표를 보인다. 「오늘 김철수 지각」이 마지막 날
      // 김철수 줄에 들어간다(2026-10-05 설계, 앱 `Day.onRowFocusEvent`).
      if (otherTable && focus && !focused) {
        throw new Error('고칠 날짜(문서)를 목록에서 찾지 못했습니다. 아무것도 고치지 않았습니다.');
      }
      await this.settleDraft(timeoutMs);
      if (otherTable && focused) await this.untilCellsOf(cellsKey, shownBefore, wanted, timeoutMs);
      // **그 문서가 열렸는지 본다.** 고른다고 한 것과 폼에 뜬 것이 다르면 남의 문서를 고친다.
      // 한두 번은 다시 고른다 — 화면의 첫 행 포커스가 늦게 끝나 덮은 것일 수 있다.
      if (focused && focus && !editsCells) {
        for (let again = 0; again < 2 && !formHolds(wanted); again += 1) {
          // eslint-disable-next-line no-await-in-loop
          await sleep(SETTLE_MS);
          // eslint-disable-next-line no-await-in-loop
          await focus(request.edit.rowsStateKey, wanted);
          // eslint-disable-next-line no-await-in-loop
          await this.settleDraft(timeoutMs);
        }
        if (!formHolds(wanted)) {
          throw new Error('고칠 문서를 골랐는데 화면이 다른 문서를 열었습니다. 아무것도 고치지 않았습니다.');
        }
      }
      if (request.edit.then) await this.focusInner(request.edit.then, timeoutMs);
      const fillHeader = async (): Promise<void> => {
        await fillConditions(request.fields, undefined, { draft: true });
        await this.settleDraft(timeoutMs);
        const missed = draftMismatches(request.fields, request.pairs);
        if (missed.length) {
          await fillConditions(
            Object.fromEntries(missed.map((key) => [key, request.fields[key] ?? ''])),
            undefined,
            { draft: true },
          );
          await this.settleDraft(timeoutMs);
        }
        verifyDraftState(request.fields, request.pairs);
      };
      if (request.edit.fieldsFirst) await fillHeader();
      if (editsCells) {
        await fillRowCells({
          stateKey: cellsKey,
          // 느슨하게 찾은 **그 행의 실제 값**으로 짚는다. 사람이 줄여 말한 이름으로는
          // 표에서 다시 못 찾는다. 다른 표를 고치는 것이면 그 표에서 다시 찾는다.
          match: request.edit.cellsMatch
            ? (matchingRow(cellsKey, request.edit.cellsMatch) ?? request.edit.cellsMatch)
            : wanted,
          cells,
        });
        await this.settleDraft(timeoutMs);
      }
      if (!request.edit.fieldsFirst) await fillHeader();
      // **고치는 문서에도 행을 더할 수 있다.** 이 분기는 `rows` 를 통째로 무시하고
      // 일찍 돌아갔다 — `오늘 올린 출장보고서에 숙박비 8만원 추가해줘` 가 행을 하나도
      // 넣지 않은 채 저장되고 "저장했습니다" 로 답했다. 화면은 그대로였다.
      if (request.rows?.items.length) {
        await fillGridRows({
          stateKey: request.rows.stateKey,
          rows: request.rows.items,
          tab: request.rows.tab,
          addLabel: request.rows.addLabel,
        });
        this.lastRows = { route: request.routePath, items: request.rows.items };
        await this.settleDraft(timeoutMs);
      }
      this.lastDraft = { route: request.routePath, fields: request.fields };
      return { routePath: request.routePath, fields: request.fields };
    }
    const copied: Record<string, string> = {};
    // 화면에 미저장 초안이 이미 있으면 계획이 뭐라 하든 그것을 이어 쓴다. `new` 를
    // 다시 누르면 앱이 "한번에 한 행만" 으로 막아 되묻기에 답한 턴이 통째로 죽는다.
    //
    // **부르는 쪽이 이어 쓰라고 하면 그대로 따른다.** 이 깃발이 `reuse` 갈래 밖에서
    // 아무 일도 안 하고 있었다 — 걸음 계획이 머리 문서를 만든 뒤 다시 내는 판에서
    // `continueDraft` 를 줬는데도 신규를 눌러 인사등록이 그 자리에서 막혔다.
    // 화면 상태로 알아보는 길(아래 두 갈래)은 상세 이름이 낯선 화면에서 못 알아본다.
    // ⚠ **이어 쓰라고 해도 「저장된 문서」 위에는 안 쓴다.**
    //
    // 이 깃발은 화면에 뭐가 떠 있든 그 위에 쓰게 했다. 그래서 신규로 시킨 일이 남의
    // 문서를 덮었다 — 출장보고서가 2026-03-05 자 「예비군」 보고서에 시험 자료를 썼고,
    // 덮어쓴 것은 되돌릴 값이 아무 데도 없다(실측 2026-09-15).
    //
    // **화면이 분명히 「저장된 문서」라고 말할 때만** 거부한다(`new === '0'`). 모르면
    // (`new` 가 없거나 빈 값이면) 예전처럼 따른다 — 상세 이름이 낯선 화면에서 못
    // 알아보는 것이 이 깃발을 무조건 따르게 만든 까닭이었고, 그 자리를 다시 깨면 안 된다.
    // 거부한 뒤에는 아래에서 미저장 행을 찾아 그것을 이어 쓴다. 그것도 없으면 신규를 누른다.
    const shown = draftState() as Record<string, unknown> | null;
    const onSavedDoc = shown !== null && String(shown.new ?? '') === '0';
    let continueExisting = (Boolean(request.continueDraft) && !onSavedDoc)
      || shown?.new === '1';
    // 상세가 비어 보여도 목록에 미저장 행이 남아 있으면 그것이 사용자의 초안이다.
    // 그 행을 다시 잡아 이어 쓴다 — 새로 만들면 화면이 막는다.
    if (!continueExisting && !request.reuse) {
      const rowsKey = unsavedRowKey();
      const focus = bridge().focusRow;
      if (rowsKey && focus && await focus(rowsKey, { new: '1' })) {
        const deadline = Date.now() + timeoutMs;
        while ((draftState() as Record<string, unknown> | null)?.new !== '1' && Date.now() < deadline) {
          await sleep(POLL_MS);
        }
        continueExisting = (draftState() as Record<string, unknown> | null)?.new === '1';
      }
    }
    let existingDraftMatch: Record<string, string> | undefined;
    /** 값을 베껴 온 과거 문서의 모습. 신규를 누른 뒤에도 이것이면 새 문서가 아니다. */
    let borrowed: string | undefined;
    if (request.reuse) {
      const focus = bridge().focusRow;
      if (!focus) throw new Error('이 앱 버전은 과거 신청 상세 조회를 지원하지 않습니다.');
      const rowsDeadline = Date.now() + timeoutMs;
      while (Date.now() < rowsDeadline) {
        const rows = bridge().pageState()?.[request.reuse.rowsStateKey];
        if (Array.isArray(rows) && rows.length > 0) break;
        await sleep(POLL_MS);
      }
      const rows = bridge().pageState()?.[request.reuse.rowsStateKey];
      if (!Array.isArray(rows) || rows.length === 0) {
        throw new Error('과거 신청 목록이 로드되지 않았습니다.');
      }
      // 과거 문서를 열면 화면이 그 행을 띄워 미저장 초안이 화면에서 밀려난다. 읽고 나서
      // 그 초안 행으로 반드시 돌아온다 — 아니면 다음 신규가 화면 규칙에 막힌다.
      if (request.continueDraft || continueExisting) {
        const drafts = rows.filter((row) => (
          row && typeof row === 'object'
          && String((row as Record<string, unknown>).new ?? '') === '1'
        ));
        if (drafts.length > 1) {
          throw new Error('미저장 휴가 초안이 여러 개입니다. 조회로 임시 행을 정리한 뒤 다시 시도해 주세요.');
        }
        if (drafts.length === 1) existingDraftMatch = { new: '1' };
      }
      const before = bridge().pageState()?.data;
      if (!await focus(request.reuse.rowsStateKey, request.reuse.match)) {
        throw new Error('조건에 맞는 과거 신청서를 찾지 못했습니다.');
      }
      const deadline = Date.now() + timeoutMs;
      while (bridge().pageState()?.data === before && Date.now() < deadline) await sleep(POLL_MS);
      while ((bridge().busy?.() ?? false) && Date.now() < deadline) await sleep(POLL_MS);
      const detail = bridge().pageState()?.data;
      if (!detail || typeof detail !== 'object') throw new Error('과거 신청 상세를 읽지 못했습니다.');
      for (const key of request.reuse.fields) {
        const value = (detail as Record<string, unknown>)[key];
        if (value !== undefined && value !== null && String(value).trim()) copied[key] = String(value);
      }
      if (existingDraftMatch) {
        const beforeDraft = bridge().pageState()?.data;
        if (!await focus(request.reuse.rowsStateKey, existingDraftMatch)) {
          throw new Error('직전 미저장 초안을 다시 선택하지 못했습니다.');
        }
        while (bridge().pageState()?.data === beforeDraft && Date.now() < deadline) await sleep(POLL_MS);
        while ((bridge().busy?.() ?? false) && Date.now() < deadline) await sleep(POLL_MS);
        continueExisting = true;
      } else {
        // **베낀 그 줄 위에 쓰지 않는다.** 아래에서 신규를 누르지만, 그것이 정말 새
        // 문서를 열었는지 아무도 확인하지 않았다 — 조용히 안 열리면 방금 연 과거
        // 문서에 그대로 채우고 저장한다.
        //
        // 실측(2026-09-02): `내일 오후 반차 신청해줘` 에 남의 기존 신청서
        // (2026-08-17 연차)가 시험 값으로 덮였다. 줄 수가 안 늘어 아무도 못 봤다.
        borrowed = JSON.stringify(detail);
      }
    }
    // 달력 화면은 화면의 `신규` 를 누르지 않는다. 그 날짜 칸을 눌러 창을 이미 열었고,
    // 행은 창 안의 `추가` 가 만든다 — 여기서 신규를 누르면 앱이
    // `기능 없음: 이 화면은 무언가를 추가하는 기능이 없습니다` 로 막는다.
    if (!continueExisting && !request.calendarDay) {
      // **방금 연 화면은 제 조회가 끝나기를 기다린 뒤 신규를 누른다.** 매출등록은 열리면
      // 스스로 목록을 받고(`InfinityRetrieve.retrieveAll`) 다 받은 뒤 첫 행에 포커스한다.
      // 그 전에 누른 신규는 새 행을 목록 맨 위에 붙이지만, 늦게 온 목록이 그것을 밀어내고
      // 저장된 문서(0003)에 포커스를 옮긴다 — 드라이버는 그 문서의 현장 칸을 채우고 있었다
      // (2026-09-25 실측: `new 1 → 0`, 1.4초 만). `edit` 갈래가 이미 이렇게 기다린다.
      if (opened) await untilQuiet(visibleGridKey(), Date.now() + timeoutMs);
      const notices = await runDraftAction('new');
      if (notices.length) throw new Error(`신규를 시작하지 못했다: ${notices.join(' / ')}`);
    }

    // 신규 API가 기존 입력 DOM을 둔 채 모델만 나중에 바꾸는 화면이 있다. 네트워크가
    // 끝나기 전에 채우면 새 모델이 도착하며 입력이 사라지므로 완료를 기다린다.
    const deadline = Date.now() + timeoutMs;
    const isBusy = bridge().busy;
    if (!continueExisting && isBusy) {
      const grace = Date.now() + BUSY_GRACE_MS;
      while (!isBusy() && Date.now() < grace) await sleep(POLL_MS);
      while (isBusy() && Date.now() < deadline) await sleep(POLL_MS);
    } else if (!continueExisting) {
      await sleep(SETTLE_MS);
    }
    if (Date.now() >= deadline) throw new Error('신규 화면 준비가 끝나지 않았다');
    // **신규가 정말 새 문서를 열었는가.** 베껴 온 과거 문서가 그대로면 여기서 멈춘다 —
    // 채우고 저장하면 그 문서가 덮인다. 되돌릴 값은 아무도 안 갖고 있다.
    if (borrowed && JSON.stringify(bridge().pageState()?.data) === borrowed) {
      throw new Error('과거 문서를 베낀 뒤 새 문서가 열리지 않았습니다.'
        + ' 그대로 저장하면 그 문서를 덮어씁니다. 화면에서 신규를 눌러 주세요.');
    }
    const fields = { ...copied, ...request.fields };
    const codeRule = NEXT_CODE[request.routePath];
    if (codeRule && !String(fields[codeRule.field] ?? '').trim()
      && !String((draftState() as Record<string, unknown> | null)?.[codeRule.field] ?? '').trim()) {
      const code = nextCodeOf(rowsOf(codeRule.listKey), codeRule.field, codeRule.digits);
      if (code) {
        fields[codeRule.field] = code;
        this.autoCode = {
          route: request.routePath, field: codeRule.field, label: codeRule.label, digits: codeRule.digits, tries: 0,
        };
      }
    }
    // 코드와 짝인 이름 칸은 첫 판에서 뺀다. 코드 팝업이 먼저 돌아야 등록된 현장이
    // 제대로 잡히고, 미등록이라 코드가 안 잡힌 것만 아래 되채우기에서 이름으로 적는다.
    const firstPass = Object.fromEntries(Object.entries(fields)
      .filter(([key]) => !pairedCode(key, fields)));
    try {
      await fillConditions(orderedDraft(firstPass), undefined, { draft: true });
    } catch (error) {
      // **채웠다고 말할 수 있는 것은 화면에 들어간 것뿐이다.** 계획이 낸 칸을 그대로
      // 읊으면 없는 값을 넣었다고 보고하게 된다 — 고장접수에서 `고장내용까지
      // 넣었습니다` 라 해 놓고 그 칸이 비어 있었고, 다음 턴이 그 거짓 위에 이어붙여
      // `고장내용을 선택하세요` 로 저장이 막혔다.
      (error as { applied?: string[] }).applied = draftApplied(fields, request.pairs);
      throw error;
    }
    // 한 칸을 바꾸면 서버에 물어보는 화면이 있다(`dw_2_itemchanged`). 그 응답은 호출
    // 시점의 값으로 상세를 통째로 덮으므로, 다음 칸을 채우는 사이에 응답이 오면 방금
    // 넣은 값이 지워진다. 잠잠해질 때까지 기다렸다가 어긋난 칸만 한 번 다시 넣는다.
    await this.settleDraft(timeoutMs);
    const missed = draftMismatches(fields, request.pairs);
    if (missed.length) {
      // 되채우기도 **첫 판과 같은 순서**여야 한다. 코드 칸을 다시 쓰면 화면이 짝 이름을
      // 지우므로, 코드부터 넣고 이름을 나중에 넣는다 — 섞어 넣으면 방금 적은 이름이
      // 사라져 `초안 입력이 화면 상태에 반영되지 않았습니다: actnm` 이 된다.
      const later = missed.filter((key) => pairedCode(key, fields));
      const first = missed.filter((key) => !later.includes(key));
      for (const group of [first, later]) {
        if (!group.length) continue;
        // eslint-disable-next-line no-await-in-loop
        await fillConditions(
          Object.fromEntries(group.map((key) => [key, fields[key] ?? ''])),
          undefined,
          { draft: true },
        );
        // eslint-disable-next-line no-await-in-loop
        await this.settleDraft(timeoutMs);
      }
    }
    verifyDraftState(fields, request.pairs);
    await this.recountTotals(fields);
    if (request.rows?.items.length) {
      await fillGridRows({
        stateKey: request.rows.stateKey,
        rows: request.rows.items,
        tab: request.rows.tab,
        addLabel: request.rows.addLabel,
      });
      this.lastRows = { route: request.routePath, items: request.rows.items };
    }
    this.lastDraft = { route: request.routePath, fields, pairs: request.pairs };
    return { routePath: request.routePath, fields };
  }

  /**
   * **그 날이 든 달을 달력에 띄운다.** 달력은 조회 조건의 달(`stmon` 같은 여섯 자리)만 그린다.
   *
   * 다른 달의 날을 그대로 열면 `openCalendarDay` 는 **지금 달의 같은 날짜 칸**을 누른다 — 「다음 달
   * 3일 당직」이 이번 달 3일에 저장된다. 사람은 달부터 넘긴다. 화면 상태에 여섯 자리 달이 꼭
   * 하나 있을 때만 그것을 조건으로 보고 바꾼다(스케쥴관리·당직계획등록은 `stmon`, 2026-10-05).
   * 그런 칸이 없거나 여럿이면 예전대로 둔다 — 모르는 칸을 바꾸지 않는다.
   */
  private async showCalendarMonth(day: string, timeoutMs: number): Promise<void> {
    const month = day.slice(0, 6);
    const state = bridge().pageState() ?? {};
    const keys = Object.entries(state)
      .filter(([, value]) => typeof value === 'string' && /^\d{4}(0[1-9]|1[0-2])$/.test(value))
      .map(([key]) => key);
    if (keys.length !== 1) return;
    const key = keys[0]!;
    if (state[key] === month) return;
    await fillConditions({ [key]: month });
    await this.settleDraft(timeoutMs);
    if (bridge().pageState()?.[key] !== month) {
      throw new Error(`달력을 ${month.slice(0, 4)}년 ${Number(month.slice(4, 6))}월로 넘기지 못했습니다. 아무것도 쓰지 않았습니다.`);
    }
  }

  /**
   * 고칠 문서가 있는 목록을 화면에 올린다.
   *
   * 화면을 열자마자 목록이 있는 것은 아니다. 사람은 조회부터 누른다 — 그러지 않으면
   * 고칠 행이 없어 `고칠 문서를 목록에서 찾지 못했습니다` 로 끝난다.
   */
  private async loadEditList(
    edit: NonNullable<PrepareRequest['edit']>,
    timeoutMs: number,
  ): Promise<void> {
    const conditions = edit.conditions ?? {};
    // **화면이 스스로 부른 조회가 끝나기를 먼저 기다린다.** 열자마자 조회를 누르면 화면의
    // 자동 조회와 겹쳐 행이 두 벌 쌓인다 — 보수현장 8,901행이 17,802행이 됐고, 같은 현장이
    // 둘로 보여 고를 수 없는 되묻기가 나갔다(2026-09-25). `retrieve` 는 이미 이렇게 한다.
    await untilQuiet(edit.rowsStateKey, Date.now() + timeoutMs);
    if (Object.keys(conditions).length) await fillConditions(conditions);
    if (Object.keys(conditions).length || !rowsOf(edit.rowsStateKey).length) {
      // 조건을 바꾸면 화면이 제 조회를 또 부른다. 그것도 끝나기를 기다린 뒤 누른다.
      if (Object.keys(conditions).length) await untilQuiet(edit.rowsStateKey, Date.now() + timeoutMs);
      await runAction('retrieve').catch(() => []);
      const deadline = Date.now() + timeoutMs;
      while (!rowsOf(edit.rowsStateKey).length && Date.now() < deadline) {
        // eslint-disable-next-line no-await-in-loop
        await sleep(POLL_MS);
      }
    }
    // **목록이 멎기를 기다린다.** 조회 직후 화면이 스스로 첫 행에 포커스하는데, 그것이
    // 우리 선택보다 늦게 끝나면 첫 행의 폼이 열린다(보수현장등록, 2026-09-25).
    await untilQuiet(edit.rowsStateKey, Date.now() + timeoutMs);
  }

  /**
   * **고른 문서의 표가 뜰 때까지 기다린다.** 문서를 고르는 목록과 칸을 고칠 표가 다른 화면에서만.
   *
   * 일근태등록은 날(`dayList`)을 고르면 그 날 직원 표(`dayDetailList`)를 서버에서 다시 받는다.
   * 앞서 보이던 날의 표에도 같은 사람이 있으므로, 새 표가 오기 전에 찾으면 **다른 날 그 사람 줄**을
   * 고친다. 그래서 표가 바뀌기(또는 바빠지기)를 잠깐 기다리고, 멎은 뒤 표의 줄이 고른 문서의 값을
   * 같은 이름으로 들고 있으면(직원 줄의 `workday`) 그 값이 맞는지 본다. 끝내 다르면 멈춘다.
   */
  private async untilCellsOf(
    cellsKey: string,
    shownBefore: unknown,
    wanted: Record<string, string>,
    timeoutMs: number,
  ): Promise<void> {
    const grace = Date.now() + BUSY_GRACE_MS;
    while (bridge().pageState()?.[cellsKey] === shownBefore
      && !(bridge().busy?.() ?? false) && Date.now() < grace) {
      // eslint-disable-next-line no-await-in-loop
      await sleep(POLL_MS);
    }
    const deadline = Date.now() + timeoutMs;
    await untilQuiet(cellsKey, deadline);
    const bare = (value: unknown): string => String(value ?? '').replace(/[^0-9A-Za-z가-힣]/g, '');
    const keys = Object.keys(wanted).filter((key) => bare(wanted[key]));
    const belongs = (): boolean => rowsOf(cellsKey).every((row) => keys.every((key) => (
      !bare(row[key]) || bare(row[key]) === bare(wanted[key]))));
    while (!belongs() && Date.now() < deadline) {
      // eslint-disable-next-line no-await-in-loop
      await sleep(POLL_MS);
    }
    if (!belongs()) {
      throw new Error('고른 날짜(문서)의 표가 화면에 뜨지 않았습니다. 아무것도 고치지 않았습니다.');
    }
  }

  /**
   * **고르면 바로 반영되는 칸**을 고칠 준비. 그 행까지 짚고 칸은 건드리지 않는다.
   *
   * 점검계획등록(`/w_tb_e035`)의 점검일자 옮기기가 이 모양이다(운영 사용 기록 2026-09-29~30,
   * 확정 1,065건 중 412건).
   *
   * 1. 달력 목록에서 그 행을 찾는다. 한 현장이 여러 날에 걸려 있으면 날마다 한 줄로 내밀어
   *    고르게 한다 — 어느 날 것을 옮기는지는 사람이 안다.
   * 2. 그 날을 눌러 창을 연다(`openCalendarDay`). 창 안의 표에서 그 행을 누른다 — 화면은
   *    누른 행의 호기 목록으로 날짜를 옮기므로 **짚는 것까지가 준비다.**
   * 3. 칸은 그대로 둔다. 확정(`commit`)이 연다.
   */
  private async prepareDeferred(request: PrepareRequest, timeoutMs: number): Promise<Prepared> {
    const edit = request.edit!;
    const cells = Object.fromEntries(Object.entries(edit.cells ?? {})
      .filter(([, value]) => String(value ?? '').trim()));
    const cellsKey = edit.cellsStateKey ?? edit.rowsStateKey;
    if (!Object.keys(cells).length) {
      throw new Error('무엇으로 바꿀지 받지 못했습니다. 아무것도 바꾸지 않았습니다.');
    }
    await this.loadEditList(edit, timeoutMs);
    const rows = readRows(edit.rowsStateKey);
    const keys = Object.keys(edit.match).filter((key) => String(edit.match[key] ?? '').trim());
    const dayOf = (row: Record<string, unknown>): string => String(row.date ?? row.plandate ?? '')
      .replace(/\D/g, '').slice(0, 8);
    const nameKey = (row: Record<string, unknown>): string | undefined => (
      ['actnm', 'cltnm', 'title', 'subject'].find((key) => String(row[key] ?? '').trim())
    );
    // **달력 목록의 현장명에는 호기 수가 붙어 온다** — `주식회사 대상순창공장[2]`(2026-09-30 개발
    // 테넌트, 앱의 `dayRemarks` 가 그 괄호를 센다). 창의 현장 표에는 없다. 사람에게도 안 보인다.
    const bare = (value: unknown): string => String(value ?? '').replace(/\s*\[\d+\]\s*$/, '').trim();
    const nameOf = (row: Record<string, unknown>): string => {
      const named = nameKey(row);
      return named ? bare(row[named]) : keys.map((key) => bare(row[key])).join(' ');
    };
    // 사람에게 내밀 이름. 날이 가르는 값이라 날을 붙인다 — `덕봉빌딩(두암정철) 2026-09-25`.
    const labelOf = (row: Record<string, unknown>): string => {
      const day = dayOf(row);
      return [nameOf(row), day ? `${day.slice(0, 4)}-${day.slice(4, 6)}-${day.slice(6, 8)}` : '']
        .filter(Boolean).join(' ');
    };
    const grouped = (list: Record<string, unknown>[]): Map<string, Record<string, unknown>> => {
      const out = new Map<string, Record<string, unknown>>();
      for (const row of list) {
        const label = labelOf(row);
        if (!out.has(label)) out.set(label, row);
      }
      return out;
    };
    // 글자가 같은 것 → 앞이 같은 것(`덕봉빌딩` = `덕봉빌딩(두암정철)`) → 가운데 든 것
    // (`대상순창공장` ⊂ `주식회사 대상순창공장`) 차례로 넓힌다. 여럿이 걸리면 고르게 한다.
    const clean = (value: string): string => value.replace(/[^0-9A-Za-z가-힣]/g, '').toLocaleLowerCase();
    const tiers = [
      (row: Record<string, unknown>, key: string) => bare(row[key]) === (edit.match[key] ?? '').trim(),
      (row: Record<string, unknown>, key: string) => sameName(bare(row[key]), edit.match[key] ?? ''),
      (row: Record<string, unknown>, key: string) => {
        const want = clean(edit.match[key] ?? '');
        return want.length >= 2 && clean(bare(row[key])).includes(want);
      },
    ];
    const close = tiers
      .map((fits) => rows.filter((row) => keys.every((key) => fits(row, key))))
      .find((found) => found.length) ?? [];
    const every = grouped(rows);
    const matched = grouped(close);
    const wanted = keys.map((key) => edit.match[key]).join(' ');
    let chosen: Record<string, unknown> | undefined;
    if (edit.pick?.trim()) chosen = every.get(edit.pick.trim());
    else if (matched.size === 1) chosen = Array.from(matched.values())[0];
    if (!chosen) {
      if (matched.size > 1) throw new OptionNotFound(wanted, Array.from(matched.keys()).slice(0, 10));
      if (every.size) throw new OptionNotFound(wanted, Array.from(every.keys()).slice(0, 10));
      throw new Error(`${wanted ? `${wanted} ` : ''}계획을 찾지 못했습니다. 그 달 목록이 비어 있습니다.`);
    }
    const label = labelOf(chosen);
    const day = dayOf(chosen);
    // **다른 달로는 옮기지 않는다.** 점검계획은 다른 달의 날을 고르면 `완료되었습니다` 를 띄우고
    // 새 달에 계획을 하나 더 만들 뿐, 원래 날 계획을 그대로 둔다 — 10월 2일 → 11월 5일이 두 날
    // 다 남았다. 같은 달 안(10월 2일 → 20일)은 깨끗이 옮겨졌다(2026-09-30 개발 테넌트, 사람과 같은
    // 달력 길). 두 벌이 되는 것은 틀린 저장이다 — 창을 열기 전에 멈춘다.
    const across = Object.values(cells).find((value) => /^\d{8}$/.test(value) && day && value.slice(0, 6) !== day.slice(0, 6));
    if (across) {
      throw new Error(`${label} 계획을 ${Number(across.slice(4, 6))}월로는 옮기지 않았습니다.`
        + ' 이 화면은 다른 달의 날을 고르면 원래 계획을 지우지 않고 그 달에 하나 더 만듭니다.'
        + ` 같은 달(${Number(day.slice(4, 6))}월) 안의 날로 말씀해 주세요.`);
    }

    // 창 안에서 다시 찾을 값. **짝이 되는 코드가 있으면 코드로 찾는다** — 이름은 목록과 창이
    // 다르게 적는다(`[2]`). 코드가 없으면 이름으로, 앞이 같은 것까지 받는다.
    const identity = keys.length ? keys : (nameKey(chosen) ? [nameKey(chosen)!] : []);
    if (!identity.length) throw new Error('고칠 행을 가리킬 이름을 받지 못했습니다.');
    const inner: Record<string, string> = {};
    for (const key of identity) {
      const code = key.replace(/nm$/i, 'cd');
      if (code !== key && String(chosen[code] ?? '').trim()) inner[code] = String(chosen[code]);
      else inner[key] = bare(chosen[key]);
    }
    const findInner = (): number => rowsOf(cellsKey).findIndex((row) => sameThing(row, inner));

    if (cellsKey !== edit.rowsStateKey && day) {
      // 앞 판이 열어 둔 창이 있으면 닫는다. 닫지 않으면 새 창이 열렸는지 못 잰다.
      await this.closeWindowOf(cellsKey, timeoutMs);
      await openCalendarDay(day);
    }
    const deadline = Date.now() + timeoutMs;
    while (findInner() < 0 && Date.now() < deadline) {
      // eslint-disable-next-line no-await-in-loop
      await sleep(POLL_MS);
    }
    const index = findInner();
    if (index < 0) throw new Error(`${label} 을 열었지만 그 행을 창에서 찾지 못했습니다. 아무것도 바꾸지 않았습니다.`);
    await untilQuiet(cellsKey, Date.now() + timeoutMs);

    // 그 행을 누른다. 바꿀 칸이 아닌 칸을 눌러야 한다 — 날짜 칸을 누르면 달력이 뜬다.
    const handle = await locateCell(cellsKey, index, identity[0]!);
    if (!handle) throw new Error(`${label} 행을 화면에서 찾지 못했습니다. 아무것도 바꾸지 않았습니다.`);
    handle.click();
    await this.settleDraft(timeoutMs);

    for (const [column, value] of Object.entries(cells)) {
      // eslint-disable-next-line no-await-in-loop
      const cell = await locateCell(cellsKey, index, column);
      const input = cell && (inputOf(cell) ?? cell.querySelector<HTMLInputElement>('input'));
      // 칸 이름(`plandate`)은 사람에게 안 보인다 — 사무직이 읽는 말에 넣지 않는다.
      if (!input) throw new Error(`${label} 행에서 바꿀 칸을 찾지 못했습니다. 아무것도 바꾸지 않았습니다.`);
      if ((input as HTMLInputElement).readOnly || (input as HTMLInputElement).disabled) {
        // 점검계획은 점검을 마친 행(상태 2)의 날짜를 잠근다.
        throw new Error(`${label} 은 화면이 그 칸을 잠가 두었습니다. 이미 끝난 일이라 바꿀 수 없습니다.`);
      }
      const current = String(rowsOf(cellsKey)[index]?.[column] ?? '').replace(/\D/g, '');
      if (current && current === value.replace(/\D/g, '')) {
        throw new Error(`${label} 은 이미 그 값입니다. 바꿀 것이 없습니다.`);
      }
    }
    const shown = rowsOf(cellsKey)[index] ?? {};
    const before = Object.fromEntries(Object.keys(cells).map((key) => [key, String(shown[key] ?? '')]));
    this.deferred = { route: request.routePath, stateKey: cellsKey, match: inner, cells };
    this.lastDraft = { route: request.routePath, fields: request.fields };
    return {
      routePath: request.routePath,
      fields: request.fields,
      deferred: { row: label, name: nameOf(chosen), cells, before },
    };
  }

  /**
   * 목록 줄에서 창을 열고 창 안에서 줄을 고른다. 창 단추는 확정이 누른다(`windowPick.ts`).
   */
  private async prepareWindow(request: PrepareRequest, timeoutMs: number): Promise<Prepared> {
    const edit = request.edit!;
    const spec = edit.window!;
    if (!edit.cellsStateKey) throw new Error('창 안 표 이름을 받지 못했습니다. 아무것도 바꾸지 않았습니다.');
    const made = await prepareWindowPick({
      routePath: request.routePath,
      rowsStateKey: edit.rowsStateKey,
      cellsStateKey: edit.cellsStateKey,
      match: edit.match,
      ...(edit.cellsMatch ? { cellsMatch: edit.cellsMatch } : {}),
      ...(edit.pick ? { pick: edit.pick } : {}),
      spec,
    }, () => this.loadEditList(edit, timeoutMs), (key) => this.closeWindowOf(key, timeoutMs), timeoutMs);
    this.windowPending = made.pending;
    this.lastDraft = { route: request.routePath, fields: request.fields };
    return {
      routePath: request.routePath,
      fields: request.fields,
      deferred: {
        row: made.row,
        name: made.name,
        cells: {},
        window: { action: spec.action, button: spec.button, picked: made.picked },
      },
    };
  }

  /**
   * 그 표를 품은 창이 떠 있으면 `닫기` 로 닫는다.
   *
   * 창을 `[role=dialog]` 로 찾지 않는다 — 그 표식은 기록기 브랜치에만 있다(2026-09-30,
   * `ModalView`). 표에서 조상으로 올라가며 **가장 가까운** `닫기` 를 찾는다. 창이 닫히면
   * 표가 DOM 에서 빠지므로(`isVisible && …`) 표가 있다는 것이 곧 창이 떠 있다는 뜻이다.
   */
  private async closeWindowOf(stateKey: string, timeoutMs: number): Promise<void> {
    const grid = onScreenAll<HTMLElement>(`[data-el-grid="${stateKey}"]`)[0];
    let scope: HTMLElement | null = grid?.parentElement ?? null;
    let close: HTMLElement | undefined;
    for (let step = 0; step < 10 && scope && !close; step += 1) {
      close = Array.from(scope.querySelectorAll<HTMLElement>('button'))
        .find((one) => (one.textContent ?? '').replace(/\s+/g, '') === '닫기');
      scope = scope.parentElement;
    }
    if (!close) return;
    // 창 단추는 화면 제목 영역이 덮고 있다 — 좌표로 누르면 막힌다. 요소를 직접 누른다.
    close.click();
    const deadline = Date.now() + timeoutMs;
    while (onScreenAll(`[data-el-grid="${stateKey}"]`).length && Date.now() < deadline) {
      // eslint-disable-next-line no-await-in-loop
      await sleep(POLL_MS);
    }
  }

  /**
   * 준비가 미뤄 둔 칸을 **지금** 바꾼다. 저장과 같은 게이트다 — Auto 가 켜졌을 때만.
   *
   * 판정은 화면으로 한다. 점검일자를 옮기면 화면이 `완료되었습니다` 를 띄우고 그 날 창을 다시
   * 여는데, 옮긴 현장은 그 날 목록에서 빠진다. 문구만 믿지 않고 **그 행이 빠졌는지**까지 본다.
   */
  private async applyDeferred(
    deferred: NonNullable<Session['deferred']>,
    auto: boolean,
    timeoutMs: number,
  ): Promise<{ notices: string[]; saved: boolean }> {
    if (!auto) throw new Error('save 는 Auto 모드가 켜져 있을 때만 할 수 있다');
    // 한 번만 쓴다. 실패해도 다시 누르지 않는다 — 누른 뒤에 무엇이 됐는지 모르는 채로 또
    // 누르면 두 번 옮긴다.
    this.deferred = undefined;
    const indexNow = (): number => rowsOf(deferred.stateKey)
      .findIndex((row) => sameThing(row, deferred.match));
    const index = indexNow();
    if (index < 0) {
      return { notices: ['바꿀 행이 화면에서 사라졌습니다. 아무것도 바꾸지 않았습니다.'], saved: false };
    }
    const notices: string[] = [];
    for (const [column, value] of Object.entries(deferred.cells)) {
      // eslint-disable-next-line no-await-in-loop
      const cell = await locateCell(deferred.stateKey, index, column);
      const input = cell && (inputOf(cell) ?? cell.querySelector<HTMLElement>('input'));
      if (!input) return { notices: [`${column} 칸을 찾지 못했습니다. 아무것도 바꾸지 않았습니다.`], saved: false };
      // 달력으로 고르는 칸만 이 길로 온다. 다른 칸이 오면 모르는 것이다 — 치지 않는다.
      if (!/^\d{8}$/.test(value.replace(/-/g, ''))) {
        return { notices: [`${column} 칸은 달력으로만 바꿀 수 있습니다. 아무것도 바꾸지 않았습니다.`], saved: false };
      }
      // eslint-disable-next-line no-await-in-loop
      await pickDate(input, value.replace(/-/g, ''));
      // eslint-disable-next-line no-await-in-loop
      notices.push(...await dismissNotices(3, timeoutMs));
    }
    await this.settleDraft(timeoutMs);
    const open = onScreenAll(`[data-el-grid="${deferred.stateKey}"]`).length > 0;
    const gone = indexNow() < 0;
    const done = notices.some((one) => /완료/.test(one)) && !notices.some(refusedNotice);
    return { notices, saved: done && open && gone };
  }

  /**
   * 금액칸을 한 번 더 건드려 화면이 **총액을 다시 세게** 한다.
   *
   * 입금입력은 금액칸이 blur 될 때 총입금액을 센다(`updateTotal`). 금액을 먼저 넣고
   * 나중에 입금형태를 끄면 화면이 그 총액을 지워 버리고 다시 세지 않는다 — 배분액은
   * 맞는데 `필수입력확인: 입금금액과 총입금액이 틀립니다` 로 저장이 막혔다.
   * 사람이 마지막에 금액칸을 한 번 보는 것과 같다.
   */
  private async recountTotals(fields: Record<string, string>): Promise<void> {
    for (const [name, value] of Object.entries(fields)) {
      if (!/amt$/i.test(name) || !String(value ?? '').trim()) continue;
      const anchor = findDraftField(name);
      const input = anchor && inputOf(anchor);
      if (!input || input.readOnly || input.disabled) continue;
      input.focus();
      input.blur();
      // eslint-disable-next-line no-await-in-loop
      await sleep(SETTLE_MS);
    }
  }

  // **기본 마감은 45초다.** 15초였을 때 실제 테넌트의 전자세금계산서 한 달치(2,234행,
  // 22초)가 「서버에서 자료를 받지 못했습니다」로 끝났다(2026-09-23). 앱이 바쁘다고
  // 알리는 동안은 서버가 일하는 중이다. 정말 죽은 서버면 답이 늦어질 뿐이지만, 받은 자료를
  // 못 받았다고 말하는 것은 틀린 말이다.
  async retrieve(request: RetrieveRequest, timeoutMs = RETRIEVE_MS): Promise<Retrieved> {
    // **시한은 조회 한 번 전체에 건다.** 예전에는 멎기 기다림 두 번과 결과 기다림이 저마다
    // `timeoutMs` 를 새로 받아 45초짜리 조회가 135초까지 갔다. 월업무보고는 화면이 내내 바빠
    // 120초를 준 조회가 372초 만에 0행으로 돌아왔고, 질문은 5분 시한을 넘겼다(2026-10-19,
    // 「지난달 실적 보여줘」). 누르기 전 기다림은 절반까지만 쓰고, 누른 뒤 결과는 남은 시간(적어도 절반)만큼 기다린다.
    const end = Date.now() + timeoutMs;
    const pressBy = Date.now() + Math.floor(timeoutMs / 2);
    await this.settleWarm();
    if (request.label in this.retrieved) {
      throw new Error(`${request.label} 이라는 이름을 이미 썼다`);
    }
    const wasAlreadyOpen = bridge().currentPath() === request.routePath;
    if (!wasAlreadyOpen) {
      await openScreen(request.routePath);
    }
    const currentState = bridge().pageState();
    const autoRefreshed = Boolean(
      request.rowsStateKey
      && this.refreshedBySiteSwitch?.route === request.routePath
      && this.refreshedBySiteSwitch.state?.[request.rowsStateKey] === currentState?.[request.rowsStateKey],
    );
    this.refreshedBySiteSwitch = null;
    if (
      // 경로는 화면의 최초 비동기 조회보다 먼저 바뀐다. 이번 요청에서 막 연 화면은
      // 기본 조건이 같아도 data=[]인 초기 상태일 수 있으므로 현재 결과를 재사용하지 않는다.
      wasAlreadyOpen
      && request.rowsStateKey
      && (!request.force || autoRefreshed)
      && selectionsMatch(request.selectionStateKeys)
      && bridge().currentPath() === request.routePath
      && !(bridge().busy?.() ?? false)
      && conditionsMatch(request.conditions)
      // 이미 받아 둔 목록도 바닥줄 수보다 짧으면 펼쳐 읽는다. 길면 겹쳐 붙은 것이라 다시 조회한다.
      && !doubledList(request.rowsStateKey)
      && await unfoldList(request.rowsStateKey, Date.now() + timeoutMs) >= 0
    ) {
      const result = {
        label: request.label,
        routePath: request.routePath,
        rowsStateKey: request.rowsStateKey,
        rows: readRows(request.rowsStateKey),
        totals: readScalars(request.scalarKeys ?? []),
        snapshot: snapshotUi(),
        applied: appliedConditions(),
      };
      this.retrieved[request.label] = result;
      return result;
    }
    // 조회는 서버 왕복이라 클릭 직후에는 이전 결과가 그대로다.
    // 배열이 새 객체로 바뀌는 것을 조회 완료 신호로 삼는다. 카탈로그가 키를
    // 모르는 화면에서는 같은 신호가 결과가 어디 있는지도 알려준다.
    //
    // 스냅샷은 조건을 다 채운 **뒤에** 뜬다. 조건 입력도 onChange 를 태우고,
    // 콤보박스 하나가 종속 목록을 다시 읽으면 그 배열 교체가 조회 완료로
    // 오인돼 이전 결과를 답이라고 내놓는다.
    // 화면에 막 들어오면 자동조회가 돌고 있다. 그 위에 조건을 얹고 조회를 누르면
    // 우리 조건이 걸리기 전 결과를 우리 것으로 읽는다 — `이번 달 만료 계약` 이
    // 13,828곳으로 나왔고, 같은 조건을 손으로 걸면 마흔여섯 행이었다.
    // 조건은 화면이 멎은 뒤에 채운다.
    //
    // **「바쁨이 꺼졌다」 한 번으로는 멎은 것이 아니다.** 여러 페이지로 받는 화면은 페이지
    // 사이에 바쁨이 잠깐 꺼진다. 그 틈에 조회를 누르면 자동 조회의 남은 페이지가 우리 결과
    // 위에 이어 붙는다 — 고장처리현황 9월 372건이 744행(모두 두 번씩)으로 잡혔다(실제
    // 테넌트, 2026-09-23). 바쁨이 `QUIET_MS` 동안 **계속** 꺼져 있고 그동안 표도 안 바뀔 때까지
    // 기다린다. 마감까지 안 멎어도 조건은 넣는다 — 조건 칸도 바쁨이 걷히기를 기다리고,
    // 조회를 누르기 직전에 한 번 더 멎기를 기다린다.
    await untilQuiet(request.rowsStateKey, pressBy);
    await fillConditions(request.conditions);
    if (request.selectionStateKeys?.length) {
      const setter = bridge().setCheckedRows;
      if (!setter) throw new Error('이 앱은 표 기반 필수 선택목록 조작을 지원하지 않는다');
      for (const key of request.selectionStateKeys) {
        const selectionDeadline = pressBy;
        const selectionReady = () => {
          const rows = bridge().pageState()?.[key];
          return Array.isArray(rows) && rows.length > 0;
        };
        while (!selectionReady() && Date.now() < selectionDeadline) {
          // 이 화면들은 상태 키를 먼저 []로 만들고 서버 응답 뒤 행을 채운다.
          // 배열 존재만 완료 신호로 보면 빈 목록을 즉시 체크해 실패하므로,
          // 실제 선택할 행이 생길 때까지 기다린다.
          // eslint-disable-next-line no-await-in-loop
          await sleep(POLL_MS);
        }
        if (!selectionReady()) {
          // 목록이 안 왔다고 질문을 끝내지 않는다. 화면 기본값으로 조회해 보고, 정말
          // 선택이 필요하면 앱이 `아무것도 선택되지 않았습니다` 로 막아 준다 —
          // 그때는 그 알림이 실패로 잡힌다. 여기서 죽으면 답할 길이 남았는데도 끝난다.
          // eslint-disable-next-line no-continue
          continue;
        }
        // 사업장을 바꾼 직후에는 화면이 다시 그려지는 중이다. 그때 표를 건드리면
        // `Unable to find node on an unmounted component` 가 나고 체크가 안 들어간다 —
        // 그다음 조회는 "매출 구분이 아무것도 선택되지 않았다" 로 막힌다.
        while ((bridge().busy?.() ?? false) && Date.now() < pressBy) {
          // eslint-disable-next-line no-await-in-loop
          await sleep(POLL_MS);
        }
        // eslint-disable-next-line no-await-in-loop
        let applied = await setter(key, 'chk', true);
        if (!applied) {
          // eslint-disable-next-line no-await-in-loop
          await sleep(600);
          // eslint-disable-next-line no-await-in-loop
          applied = await setter(key, 'chk', true);
        }
        if (!applied) throw new Error(`필수 선택목록 ${key} 를 체크하지 못했다`);
      }
    }
    const before = { ...(bridge().pageState() ?? {}) };
    // 화면 진입 자동조회가 선택목록 준비 전에 띄운 선행조건 알림은 이미 닫혔다.
    // 조건과 필수 선택을 채운 뒤 누르는 이번 조회의 알림만 결과로 판정한다.
    // **조건을 바꾸면 스스로 조회하는 화면이 많다.** 날짜 칸이 바뀌자마자 그 화면의 조회가
    // 돌고, 그 위에 우리 조회를 누르면 앞 조회의 남은 페이지가 우리 결과 위에 이어 붙는다.
    // 조회를 누르기 직전에도 멎기를 기다린다(2026-09-23, 고장처리현황 372건이 744행).
    //
    // **마감까지 안 멎어도 누른다 — 멈추지 않는다.** 한때 두 번 기다려도 바쁘면 던졌는데,
    // 전자세금계산서발행(실제 테넌트, 2026-10-07 아침)에서 90초 내내 바빠 그 오류가 나갔고
    // 사람에게는 「AI 서비스에 연결하지 못했습니다」로 갔다. 같은 화면을 따로 재면 3초에
    // 끝났다 — 무엇이 바쁨을 붙들었는지 모르는 채 멈추는 것은 근거가 없다. 겹쳐 붙은
    // 목록은 결과를 읽을 때(`complete`) 가려 다시 조회한다. 대답이 끝내 안 오면 `answered`
    // 가 거짓으로 나가 「자료가 없다」고 단언하지 않는다.
    await untilQuiet(request.rowsStateKey, pressBy);
    let notices = await runAction('retrieve', false);
    const blocking = notices.find(isBlockingNotice);
    if (blocking && !selectionsMatch(request.selectionStateKeys)) {
      throw new Error(`조회가 선행조건 알림으로 차단됐다: ${blocking}`);
    }
    if (blocking) {
      // 자동조회 알림이 늦게 도착해 명시적 조회 뒤에 잡힌 경우다. 현재 필수 목록이
      // 모두 선택되어 있으면 이번 조회의 실패가 아니며 사용자 알림에도 남기지 않는다.
      notices = notices.filter((notice) => notice !== blocking);
    }

    // 누른 뒤에는 적어도 절반을 기다린다 — 누르기(알림 확인 포함)가 시간을 써도 결과를 볼 틈은 남긴다.
    const deadline = Math.max(end, Date.now() + Math.floor(timeoutMs / 2));

    // 앱이 "서버를 기다리는 중"을 알려주고 읽을 표까지 정해져 있으면, 배열이
    // 바뀌는지 볼 이유가 없다. 앱이 끝났다고 하면 끝난 것이다. 같은 조건으로
    // 다시 조회해 내용이 그대로일 때도 이쪽은 멎지 않는다.
    if (bridge().busy && request.rowsStateKey) {
      const settled = await this.complete(
        request.rowsStateKey,
        deadline,
        await this.settle(request.rowsStateKey, deadline, before[request.rowsStateKey]),
        notices,
      );
      const result = {
        label: request.label,
        routePath: request.routePath,
        rowsStateKey: request.rowsStateKey,
        rows: settled.rows,
        answered: settled.answered,
        totals: await lateTotals(request.scalarKeys ?? [], settled.rows.length, deadline),
        notices,
        snapshot: snapshotUi(),
        applied: appliedConditions(),
      };
      this.retrieved[request.label] = result;
      return result;
    }

    while (Date.now() < deadline) {
      const changed = changedArrays(before, bridge().pageState());
      // 부르는 쪽이 표를 지정했으면 **그것이 바뀔 때까지** 기다린다. 조건을
      // 바꾸면 필터 목록이 결과보다 먼저 갱신되는 화면이 있어, 아무거나 잡으면
      // 필터를 결과라고 내놓는다. 월매출매입현황에서 실제로 그렇게 나왔다 —
      // 매출 대신 매출구분 목록을 답이라고 돌려주었다.
      const wanted = request.rowsStateKey;
      const key = wanted
        ? (changed.includes(wanted) ? wanted : undefined)
        : changed[0];
      if (key) {
        // 바뀌었다고 바로 읽으면 안 된다. 화면은 조회를 시작할 때 표를 먼저
        // 비우고 서버 응답이 온 뒤에 채운다 — 비우는 것도 새 배열이라 교체로
        // 보인다. 잠잠해질 때까지 기다려야 빈 배열을 결과라고 내놓지 않는다.
        const settled = await this.complete(key, deadline, await this.settle(key, deadline, before[key]), notices);
        const result = {
          label: request.label,
          routePath: request.routePath,
          rowsStateKey: key,
          rows: settled.rows,
          answered: settled.answered,
          totals: await lateTotals(request.scalarKeys ?? [], settled.rows.length, deadline),
          notices,
          snapshot: snapshotUi(),
          applied: appliedConditions(),
        };
        this.retrieved[request.label] = result;
        return result;
      }
      await sleep(POLL_MS);
    }
    throw new Error(`${request.label} 조회가 ${timeoutMs}ms 안에 끝나지 않았다`);
  }

  async sweepDetail(request: SweepRequest, timeoutMs = SWEEP_MS): Promise<Retrieved> {
    await this.settleWarm();
    const end = Date.now() + timeoutMs;
    const master = await this.retrieve({
      label: `${request.label}__master`,
      routePath: request.routePath,
      rowsStateKey: request.masterStateKey,
      conditions: request.conditions,
      // 같은 조건이 떠 있어도 새로 받는다 — 남아 있는 위 표는 사람이 다른 행을 고른 뒤의 것일 수 있다.
      force: true,
    }, Math.floor(timeoutMs / 2));
    const { range } = request;
    // 숫자만 남겨 경계의 자리수만큼 견준다 — 모델이 `2026-10-15` 를 내면 글자 비교로는 전부 걸러졌다(검토, 2026-10-21).
    const digits = (value: unknown) => String(value ?? '').replace(/\D/g, '');
    const from = digits(range?.from);
    const to = digits(range?.to);
    const only = request.only
      ? { key: request.only.key, wanted: new Set(request.only.values.map((value) => String(value).trim())) }
      : undefined;
    const inRange = master.rows.filter((row) => {
      if (!range) return true;
      const value = digits(row[range.key]);
      return (!from || value.slice(0, from.length) >= from) && (!to || value.slice(0, to.length) <= to);
    }).filter((row) => !only || only.wanted.has(String(row[only.key] ?? '').trim()));
    const limit = request.limit ?? SWEEP_LIMIT;
    // 상세가 빈 행도 셀 수 있게 범위 안 위 표 행을 같이 준다 — 「최근 입사자 중 경력직 비율」은
    // 경력이 없는 사람이 분모에 있어야 한다. 상세 행만 주면 그 사람들이 사라진다.
    // 읽은 행에는 상세가 몇 줄이었는지(`detailRows`) 붙인다 — 계산이 위 표를 상세로 잘못 읽고
    // 「4명 모두 경력직」이라 답했다(2026-10-21). 읽지 못한 행에는 안 붙인다(0 과 모름은 다르다).
    const detailCounts = new Map<number, number>();
    const masterRows = (): Record<string, unknown>[] => inRange.map((row, index) => ({
      ...plainCells(row),
      ...(detailCounts.has(index) ? { detailRows: detailCounts.get(index) } : {}),
    }));
    let missed = 0;
    let unchanged = 0;
    // 좁힌 값 중 위 목록에 없는 것 — 사원명부의 퇴사자는 인사등록 재직 목록에 없다. 세어 두지 않으면 분모가
    // 조용히 어긋난다(검토, 2026-10-21).
    const unmatched = only
      ? Array.from(only.wanted).filter((value) => !inRange.some((row) => String(row[only.key] ?? '').trim() === value)).length
      : 0;
    const result = (
      rows: Record<string, unknown>[], read: number, reason?: string, withMasters = true,
    ): Retrieved => {
      const out: Retrieved = {
        label: request.label,
        routePath: request.routePath,
        rowsStateKey: request.detailStateKey,
        rows,
        // 위 목록이 길어 안 펼쳤거나 시한에 걸린 것은 서버가 대답을 안 한 것이 아니다 — 그 사정은
        // `swept.reason` 이 말한다. 거짓으로 두면 런타임이 「서버 무응답」으로 읽고 같은 조회를 되풀이한다.
        answered: master.answered !== false,
        notices: master.notices,
        // 안 펼쳤으면 위 표 행도 안 준다 — 상세 없는 대상으로 세면 「모두 비경력」이 된다(검토, 2026-10-21).
        masterRows: withMasters ? masterRows() : [],
        swept: {
          masterRows: inRange.length, read, missed, unchanged,
          ...(unmatched ? { unmatched } : {}), ...(reason ? { reason } : {}),
        },
      };
      this.retrieved[request.label] = out;
      return out;
    };
    // 너무 많으면 한 줄도 고르지 않는다 — 한 줄에 1초 남짓이라 백 줄이면 질문이 시한을 넘긴다.
    if (inRange.length > limit) {
      return result([], 0, `위 목록이 ${inRange.length}건이라 다 펼치지 않았다(한 번에 ${limit}건까지)`, false);
    }
    // 좁힌 값이 하나도 안 맞으면 대개 번호 꼴이 다른 것이다(`00830` 과 `p00830`) — 「상세 없음」이 아니다.
    if (only && only.wanted.size && !inRange.length) {
      return result([], 0, `찾을 대상 ${only.wanted.size}건이 위 목록에 하나도 없었다`, false);
    }
    const focus = bridge().focusRow;
    if (!focus) throw new Error('이 앱은 표의 행을 고르는 기능을 지원하지 않는다');
    if (request.tab && inRange.length) {
      // 탭을 못 열면 턴을 죽이지 않는다 — 권한이 없거나 탭 이름이 바뀐 화면이다.
      try {
        await pressTab(request.tab);
      } catch (error) {
        return result([], 0, `${request.tab} 탭을 열지 못했다(${String((error as Error)?.message ?? error).slice(0, 80)})`, false);
      }
    }
    const rows: Record<string, unknown>[] = [];
    const rowMs = request.rowMs ?? SWEEP_ROW_MS;
    const changed = (before: unknown) => bridge().pageState()?.[request.detailStateKey] !== before;
    let read = 0;
    // 번호로 돈다 — 앱은 downlevelIteration 없이 굽는다(`entries()` 를 못 돈다).
    for (let index = 0; index < inRange.length; index += 1) {
      const row = inRange[index]!;
      if (Date.now() >= end) break;
      const cells = plainCells(row);
      const match = request.matchKeys?.length
        ? Object.fromEntries(request.matchKeys.filter((key) => key in cells).map((key) => [key, cells[key]!]))
        : cells;
      const before = bridge().pageState()?.[request.detailStateKey];
      // eslint-disable-next-line no-await-in-loop
      const picked = await focus(request.masterStateKey, match);
      read += 1;
      if (!picked) {
        missed += 1;
        // eslint-disable-next-line no-continue
        continue;
      }
      // 그 행의 상세가 올 때까지 — 안 바뀌면 0행으로 치고 넘어간다(남은 상세는 앞 행 것이다).
      const until = Math.min(end, Date.now() + rowMs);
      while (!changed(before) && Date.now() < until) {
        // eslint-disable-next-line no-await-in-loop
        await sleep(POLL_MS);
      }
      // 기다림을 넘겨도 화면이 아직 받는 중이면 그것은 이 행의 상세다. 다음 행으로 가면 늦게 온 이 행 것이
      // 다음 행의 「바뀐 상세」로 잡혀 다음 행 칸이 붙는다(검토, 2026-10-21).
      while (!changed(before) && (bridge().busy?.() ?? false) && Date.now() < end) {
        // eslint-disable-next-line no-await-in-loop
        await sleep(POLL_MS);
      }
      if (!changed(before)) {
        unchanged += 1;
        // eslint-disable-next-line no-continue
        continue;
      }
      // eslint-disable-next-line no-await-in-loop
      await untilQuiet(request.detailStateKey, Math.min(end, Date.now() + rowMs));
      const details = readRows(request.detailStateKey);
      detailCounts.set(index, details.length);
      for (const detail of details) rows.push({ ...cells, ...detail });
    }
    // 시한에 걸려 다 못 읽었으면 그렇다고 말한다 — 안 말하면 앞 몇 날만으로 낸 숫자가 전 기간처럼 나간다.
    return result(rows, read, read < inRange.length ? `위 목록 ${inRange.length}건 중 ${read}건만 읽었다(시간이 모자랐다)` : undefined);
  }

  /**
   * **받은 목록을 다 읽는다.** 자리 잡은 표가 바닥줄의 총 수보다 짧으면 끝까지 펼치고,
   * 길면 두 조회가 겹쳐 붙은 것이니 멎은 뒤 한 번 다시 조회한다.
   *
   * 나눠 보여 주는 목록(`InfinityRetrieve`)은 서버에서 다 받고도 50행만 상태에 올린다 —
   * 점검결과등록 10월 계획 `총 2,176행` 을 50행으로 답했다(실제 테넌트, 2026-10-06).
   * 겹친 조회는 두 응답을 다 이어 붙인다 — 같은 화면이 300행 중 서로 다른 150행을 줬다
   * (벤치, 2026-10-06). 앞 절반과 뒤 절반이 같거나, 바닥줄 수(응답 하나의 것)보다 행이
   * 많으면 겹친 것이다. 바닥줄 수만으로는 못 가린다 — 화면이 처음에 몇 쪽을 한꺼번에
   * 올리면(앞서 고른 행까지, `retrieveTo`) 150행 두 벌도 `총 2,326행` 보다 적다.
   */
  private async complete(
    key: string,
    // 펼치기·다시 조회는 제 몫의 시간을 따로 쓴다(아래) — 조회 마감은 받기만 하고 안 쓴다.
    // 앱(elmanpro)은 안 쓰는 매개변수를 오류로 본다(TS6133) — 이름에 `_` 를 붙인다.
    _deadline: number,
    settled: { rows: Record<string, unknown>[]; answered: boolean },
    notices: string[],
  ): Promise<{ rows: Record<string, unknown>[]; answered: boolean }> {
    if (!settled.answered) return settled;
    let current = settled;
    for (let again = 0; ; again += 1) {
      const shown = readRows(key);
      const doubled = doubledList(key);
      if (!doubled || again >= 1) {
        // 펼치기는 겹침을 가린 **뒤에** 한다. 겹친 목록을 펼치면 남은 쪽이 그 위에 또 붙는다.
        // 펼치기는 서버를 안 다녀오고 쪽마다 나아가므로 조회 마감과 따로 제 몫의 시간을 준다 —
        // 서버가 20초를 쓴 10월 계획이 남은 25초 안에 2,175행 중 650행만 펼쳤다.
        // eslint-disable-next-line no-await-in-loop
        if (!doubled) await unfoldList(key, Date.now() + UNFOLD_MS);
        return { rows: readRows(key), answered: current.answered };
      }
      // eslint-disable-next-line no-await-in-loop
      // 다시 조회는 제 몫의 시간을 갖는다 — 겹침은 대개 앞 조회가 마감을 다 쓴 뒤에 생긴다.
      const again2 = Date.now() + RETRIEVE_MS;
      if (!(await untilQuiet(key, again2))) return { rows: shown, answered: current.answered };
      const before = bridge().pageState()?.[key];
      // eslint-disable-next-line no-await-in-loop
      notices.push(...(await runAction('retrieve', false)).filter((one) => !notices.includes(one)));
      // eslint-disable-next-line no-await-in-loop
      current = await this.settle(key, again2, before);
      if (!current.answered) return current;
    }
  }

  /**
   * 결과를 읽어도 되는 때까지 기다린다.
   *
   * 앱이 서버를 기다리는 중인지 알려주면 그것을 쓴다. 화면 상태만 보고
   * 알아내려 하면 틀린다 — 조회를 누르면 표를 먼저 비우므로, 빈 채로 서버를
   * 기다리는 것과 정말 결과가 없는 것이 구분되지 않는다. 실제로 그렇게
   * 월매출매입현황이 7행 대신 0행을 돌려주었다.
   *
   * 그 신호가 없는 앱에서는 배열이 잠잠해지는 것으로 대신한다.
   */
  private async settle(
    key: string,
    deadline: number,
    wasThere: unknown,
  ): Promise<{ rows: Record<string, unknown>[]; answered: boolean }> {
    const isBusy = bridge().busy;

    // 신호가 둘이고 각자 못 보는 것이 있다.
    //
    // `busy` 는 결과가 그대로일 때도 끝을 알려준다. 대신 **켜지기 전에 물어보면
    // 거짓이다** — 유예 안에 안 켜지면 예전 코드는 그 길로 빠져나가 요청이 나가기도
    // 전의 화면을 읽었다. 설치현장 질문이 조회가 끝나기 전에 답한 것이 이것이다.
    //
    // 표 참조가 바뀌는 것은 늦게 시작해도 잡아낸다. 조회를 누르면 앱이 표를 먼저
    // 비우기 때문이다. 대신 같은 조건으로 다시 조회해 내용이 그대로면 영영 안 바뀐다.
    //
    // 그래서 **실제로 바빠졌으면 busy 를 믿고, 끝내 안 바빠졌으면 표가 바뀌기를
    // 기다린다.**
    let becameBusy = false;
    if (isBusy) {
      const started = Date.now();
      while (!isBusy() && Date.now() - started < BUSY_GRACE_MS) await sleep(POLL_MS);
      becameBusy = isBusy();
      while (isBusy() && Date.now() < deadline) await sleep(POLL_MS);
    }
    if (!becameBusy) {
      while (bridge().pageState()?.[key] === wasThere && Date.now() < deadline) {
        await sleep(POLL_MS);
      }
    }

    // 어느 쪽이든 마지막에는 표가 조용해질 때까지 기다린다. 도중에 다시 바빠지면
    // (이어지는 두 번째 요청) 처음부터 센다.
    let previous = bridge().pageState()?.[key];
    let quiet = 0;
    while (quiet < SETTLE_MS && Date.now() < deadline) {
      await sleep(POLL_MS);
      if (isBusy && isBusy()) { quiet = 0; continue; }
      const current = bridge().pageState()?.[key];
      if (current === previous) quiet += POLL_MS;
      else { previous = current; quiet = 0; }
    }
    // **마감까지 바쁜 채로 끝났으면 서버가 대답을 안 한 것이다.** 이때 읽는 빈 배열은
    // 「조회를 눌러 표를 비운 상태」이지 결과가 아니다. 이 한 줄이 없으면 런타임이
    // 그것을 `empty-result` 로 읽고 「자료가 없습니다」라고 단언한다.
    //
    // **행이 왔으면 대답한 것이다.** 화면은 우리 조회 말고 다른 이유로도 바쁠 수 있어
    // (배경 갱신·이어지는 요청), `busy` 하나만 보면 멀쩡히 받은 자료를 「서버가 안
    // 줬다」로 뒤집는다. 자료가 손에 있는데 못 받았다고 말하는 것이 더 나쁘다.
    const rows = readRows(key);
    const answered = rows.length > 0 || !(isBusy?.() ?? false);
    return { rows, answered };
  }

  results(): Record<string, Retrieved> {
    return { ...this.retrieved };
  }
}
